import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { API } from '../../utils/constants';
import { CalendarPreviewComponent } from './CalendarModule';

const JobPhotos = ({ job, onBack }) => {
  const [photos, setPhotos] = useState({
    pre_work_photos: [],
    progress_photos: [],
    completion_photos: [],
    total_photos: 0
  });
  const [loading, setLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/jobs/${job.id}/photos`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Error fetching photos:', error);
      alert('Error loading photos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!showDeleteConfirm) return;
    
    const { photoType, photoIndex } = showDeleteConfirm;
    try {
      await axios.delete(`${API}/jobs/${job.id}/photos/${photoType}/${photoIndex}`);
      alert('Photo deleted successfully!');
      fetchPhotos(); // Refresh photos
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Error deleting photo. Please try again.');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    try {
      return new Date(timestamp).toLocaleString();
    } catch (error) {
      return 'Invalid date';
    }
  };

  const PhotoSection = ({ title, photos, photoType, icon, bgColor }) => {
    if (!photos || photos.length === 0) {
      return (
        <div className={`${bgColor} rounded-lg p-6 border border-gray-200`}>
          <h3 className="text-lg font-bold text-gray-700 mb-2 flex items-center gap-2">
            {icon} {title}
          </h3>
          <p className="text-gray-500 text-sm">No {title.toLowerCase()} uploaded yet.</p>
        </div>
      );
    }

    return (
      <div className={`${bgColor} rounded-lg p-6 border border-gray-200`}>
        <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
          {icon} {title} ({photos.length})
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="aspect-square relative cursor-pointer" onClick={() => setSelectedPhoto({ ...photo, photoType, photoIndex: index })}>
                <img 
                  src={photo.photo} 
                  alt={photo.description || 'Job photo'}
                  className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all flex items-center justify-center">
                  <span className="text-white opacity-0 hover:opacity-100 text-sm font-medium">Click to view</span>
                </div>
              </div>
              <div className="p-3">
                <p className="text-xs text-gray-600 mb-1 line-clamp-2">{photo.description || 'No description'}</p>
                <p className="text-xs text-gray-500">By {photo.uploaded_by || 'Unknown'}</p>
                <p className="text-xs text-gray-500">{formatTimestamp(photo.timestamp)}</p>
                <button
                  onClick={() => setShowDeleteConfirm({ photoType, photoIndex: index })}
                  className="mt-2 text-xs text-red-600 hover:text-red-800 font-medium"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
  );
};

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
          <h1 className="text-3xl font-bold text-gray-800">📸 Job Photos - {job.customer_name}</h1>
          <div className="text-right">
            <p className="text-lg font-semibold text-gray-700">Total Photos: {photos.total_photos}</p>
          </div>
        </div>

        {/* Job Info */}
        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-2">{job.customer_name}</h2>
          <p className="text-gray-600">📍 {job.job_location}</p>
          {job.po_number && <p className="text-blue-600 text-sm mt-1">PO: {job.po_number}</p>}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading photos...</p>
          </div>
        ) : (
          <div className="space-y-6">
            <PhotoSection 
              title="Pre-Work Photos" 
              photos={photos.pre_work_photos} 
              photoType="pre_work"
              icon="🏗️"
              bgColor="bg-blue-50"
            />
            <PhotoSection 
              title="Progress Photos" 
              photos={photos.progress_photos} 
              photoType="progress"
              icon="⚡"
              bgColor="bg-yellow-50"
            />
            <PhotoSection 
              title="Completion Photos" 
              photos={photos.completion_photos} 
              photoType="completion"
              icon="✅"
              bgColor="bg-green-50"
            />
          </div>
        )}

        {/* Photo Modal */}
        {selectedPhoto && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto">
            <div className="bg-white rounded-xl max-w-4xl max-h-[90vh] overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800">Photo Details</h3>
                <button 
                  onClick={() => setSelectedPhoto(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="p-4">
                <img 
                  src={selectedPhoto.photo} 
                  alt={selectedPhoto.description || 'Job photo'}
                  className="max-w-full max-h-96 mx-auto rounded-lg shadow-lg"
                />
                <div className="mt-4 space-y-2">
                  <p className="text-gray-700"><span className="font-semibold">Description:</span> {selectedPhoto.description || 'No description provided'}</p>
                  <p className="text-gray-700"><span className="font-semibold">Category:</span> {selectedPhoto.photoType?.replace('_', ' ')}</p>
                  <p className="text-gray-700"><span className="font-semibold">Uploaded by:</span> {selectedPhoto.uploaded_by || 'Unknown'}</p>
                  <p className="text-gray-700"><span className="font-semibold">Date:</span> {formatTimestamp(selectedPhoto.timestamp)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Confirm Delete</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this photo? This action cannot be undone.</p>
              <div className="flex gap-4">
                <button
                  onClick={handleDeletePhoto}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium"
                >
                  Delete Photo
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        </div>
      </div>
  );
};


// Job Work Order Component (single file per job)
const JobWorkOrder = ({ job, onBack, currentUser }) => {
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploaderName, setUploaderName] = useState('');

  useEffect(() => {
    fetchWorkOrder();
  }, []);

  const fetchWorkOrder = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/work-order`);
      if (res.data?.exists) {
        setWorkOrder(res.data.document);
      } else {
        setWorkOrder(null);
      }
    } catch (e) {
      console.error('Error fetching work order', e);
      setWorkOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const viewDoc = (doc) => {
    try {
      if (!doc?.file_data) {
        alert('No work order file available.');
        return;
      }
      const byteCharacters = atob(doc.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: doc.file_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('Error viewing work order', err);
      alert('Error viewing work order.');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 10MB limit (same as other docs)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('Please upload a PDF or image (png/jpg/webp).');
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Please choose a file.');
      return;
    }
    if (!uploaderName.trim()) {
      alert('Please enter your name.');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      const base64Data = await new Promise((resolve, reject) => {
        reader.onload = (event) => {
          try {
            resolve(event.target.result.split(',')[1]);
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      await axios.post(`${API}/jobs/${job.id}/work-order`, {
        filename: selectedFile.name,
        file_type: selectedFile.type,
        file_data: base64Data,
        uploaded_by: uploaderName.trim(),
      });

      setSelectedFile(null);
      setUploaderName('');
      await fetchWorkOrder();
      alert('Work order uploaded.');
    } catch (err) {
      console.error('Error uploading work order', err);
      alert(err?.response?.data?.detail || 'Error uploading work order.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete the work order for this job?')) return;
    try {
      await axios.delete(`${API}/jobs/${job.id}/work-order`);
      setWorkOrder(null);
      alert('Work order deleted.');
    } catch (err) {
      console.error('Error deleting work order', err);
      alert(err?.response?.data?.detail || 'Error deleting work order.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-gray-600 hover:text-gray-800 text-sm flex items-center gap-1">
            <span className="text-lg">←</span>
            <span>Back to Jobs</span>
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">Work Order</h1>
          <div />
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <div className="text-sm font-semibold text-gray-800">
            {job?.customer_name || 'Job'}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            {job?.job_location || ''}
          </div>

          <div className="mt-4">
            {loading ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : workOrder ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-gray-200 p-3">
                <div>
                  <div className="text-sm font-medium text-gray-900">{workOrder.filename}</div>
                  <div className="text-xs text-gray-600">
                    Uploaded by {workOrder.uploaded_by} • {workOrder.upload_date ? new Date(workOrder.upload_date).toLocaleString() : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => viewDoc(workOrder)}
                    className="rounded bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
                  >
                    View
                  </button>
                  {currentUser?.rawRole === 'admin' ? (
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="rounded border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">No work order uploaded for this job.</div>
            )}
          </div>

          {currentUser?.rawRole === 'admin' ? (
            <div className="mt-6 border-t border-gray-200 pt-4">
              <div className="text-sm font-semibold text-gray-800 mb-2">Upload / Replace Work Order</div>
              <form onSubmit={handleUpload} className="space-y-3">
                <input
                  type="file"
                  accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleFileSelect}
                  className="block w-full text-xs"
                />
                <input
                  type="text"
                  value={uploaderName}
                  onChange={(e) => setUploaderName(e.target.value)}
                  placeholder="Your name"
                  className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                />
                <button
                  type="submit"
                  disabled={uploading}
                  className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:bg-emerald-300"
                >
                  {uploading ? 'Uploading…' : 'Upload Work Order'}
                </button>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};


// Job Documents Component
const JobDocuments = ({ job, onBack }) => {
  const [documents, setDocuments] = useState([]);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    filename: '',
    file_type: '',
    file_data: '',
    description: '',
    category: 'General',
    uploaded_by: ''
  });

  const documentCategories = [
    'General',
    'Utility Locates', 
    'Engineered Drawings',
    'Photos',
    'Permits',
    'Insurance',
    'Contracts',
    'Site Plans'
  ];

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${job.id}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Data = event.target.result.split(',')[1]; // Remove data:type;base64, prefix
        setUploadForm(prev => ({
          ...prev,
          filename: file.name,
          file_type: file.type,
          file_data: base64Data
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file_data || !uploadForm.uploaded_by) {
      alert('Please select a file and enter your name');
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/documents`, {
        ...uploadForm,
        job_id: job.id
      });
      
      // Reset form
      setUploadForm({
        filename: '',
        file_type: '',
        file_data: '',
        description: '',
        category: 'General',
        uploaded_by: ''
      });
      setShowUploadForm(false);
      fetchDocuments();
      alert('Document uploaded successfully!');
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Error uploading document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (confirm('Are you sure you want to delete this document?')) {
      try {
        await axios.delete(`${API}/jobs/${job.id}/documents/${documentId}`);
        fetchDocuments();
        alert('Document deleted successfully!');
      } catch (error) {
        console.error('Error deleting document:', error);
        alert('Error deleting document. Please try again.');
      }
    }
  };

  const viewDocument = (doc) => {
    try {
      console.log('ViewDocument called with:', {
        filename: doc.filename,
        fileType: doc.file_type,
        dataLength: doc.file_data ? doc.file_data.length : 0
      });
      
      if (!doc.file_data) {
        alert('No document data available to view.');
        return;
      }
      
      const byteCharacters = atob(doc.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: doc.file_type || 'application/octet-stream' });
      
      console.log('Created blob:', {
        size: blob.size,
        type: blob.type
      });
      
      const url = URL.createObjectURL(blob);
      console.log('Created blob URL:', url);
      
      // Try to open in new tab with better error handling
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      
      if (!newWindow) {
        // Popup was blocked, try alternative approach
        console.warn('Popup blocked, trying alternative approach');
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Clean up after a delay to allow the browser to load the file
      setTimeout(() => {
        URL.revokeObjectURL(url);
        console.log('Cleaned up blob URL');
      }, 5000); // Increased timeout to 5 seconds
    } catch (error) {
      console.error('Error viewing document:', error);
      alert(`Error viewing document: ${error.message}`);
    }
  };

  const downloadDocument = (doc) => {
    try {
      console.log('DownloadDocument called with:', {
        filename: doc.filename,
        fileType: doc.file_type,
        dataLength: doc.file_data ? doc.file_data.length : 0
      });
      
      if (!doc.file_data) {
        alert('No document data available to download.');
        return;
      }
      
      const byteCharacters = atob(doc.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: doc.file_type || 'application/octet-stream' });
      
      console.log('Created download blob:', {
        size: blob.size,
        type: blob.type,
        filename: doc.filename
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Clean up
      setTimeout(() => {
        URL.revokeObjectURL(url);
        console.log('Cleaned up download blob URL');
      }, 1000);
      
      console.log('Download initiated for:', doc.filename);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert(`Error downloading document: ${error.message}`);
    }
  };

  const formatFileSize = (base64String) => {
    const bytes = (base64String.length * 3) / 4;
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
          <h1 className="text-3xl font-bold text-gray-800">📁 Job Documents - {job.customer_name}</h1>
          <button 
            onClick={() => setShowUploadForm(true)}
            className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-xl font-bold text-white shadow-lg"
          >
            + Upload Document
          </button>
        </div>

        {/* Job Info */}
        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-2">{job.customer_name}</h2>
          <p className="text-gray-600">📍 {job.job_location}</p>
          {job.po_number && <p className="text-blue-600 text-sm mt-1">PO: {job.po_number}</p>}
        </div>

        {/* Upload Form Modal */}
        {showUploadForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
            <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Upload Document</h2>
              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select File</label>
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 file:bg-blue-600 file:text-white file:border-none file:rounded file:px-3 file:py-2 file:mr-3"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.dwg,.dxf"
                    required
                  />
                  <p className="text-xs text-gray-600 mt-1">Supported: PDF, DOC, JPG, PNG, DWG (Max 10MB)</p>
                </div>
                
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                  required
                >
                  {documentCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                />
                
                <input
                  type="text"
                  placeholder="Uploaded by (your name)"
                  value={uploadForm.uploaded_by}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, uploaded_by: e.target.value }))}
                  className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                  required
                />
                
                <div className="flex gap-4 pt-4">
                  <button 
                    type="submit" 
                    disabled={uploading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white"
                  >
                    {uploading ? 'Uploading...' : 'Upload Document'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowUploadForm(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Documents List */}
        <div className="space-y-4">
          {documents.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <div className="text-6xl mb-4">📁</div>
              <p>No documents uploaded yet.</p>
              <p className="text-sm">Click "Upload Document" to add files.</p>
            </div>
          )}

          {documents.map((doc) => (
            <div key={doc.id} className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="text-3xl">
                      {doc.file_type?.startsWith('image/') ? '🖼️' : 
                       doc.file_type === 'application/pdf' ? '📄' : 
                       doc.filename.endsWith('.dwg') || doc.filename.endsWith('.dxf') ? '📐' : '📋'}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{doc.filename}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                          {doc.category}
                        </span>
                        <span className="text-gray-500 text-sm">
                          {formatFileSize(doc.file_data)}
                        </span>
                      </div>
                    </div>
                  </div>
                  {doc.description && (
                    <p className="text-gray-600 text-sm mb-2 italic">"{doc.description}"</p>
                  )}
                  <p className="text-gray-500 text-sm">
                    📤 Uploaded by <span className="font-medium">{doc.uploaded_by}</span> on {new Date(doc.upload_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 lg:gap-3">
                  <button
                    onClick={() => viewDocument(doc)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200 flex items-center justify-center gap-1"
                  >
                    👁️ View
                  </button>
                  <button
                    onClick={() => downloadDocument(doc)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200 flex items-center justify-center gap-1"
                  >
                    📥 Download
                  </button>
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200 flex items-center justify-center gap-1"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        </div>
      </div>
  );
};

// Requirements Status Editor Modal Component
const RequirementsEditorModal = ({ job, onClose, onUpdate }) => {
  const [requirements, setRequirements] = useState({
    requirements_utility_locates: job?.requirements_utility_locates || false,
    requirements_permit: job?.requirements_permit || false,
    requirements_materials: job?.requirements_materials || false,
    requirements_accommodations: job?.requirements_accommodations || false,
    requirements_other: job?.requirements_other || false,
    requirements_other_description: job?.requirements_other_description || '',
    status_utility_locates: job?.status_utility_locates || 'red',
    status_permit: job?.status_permit || 'red',
    status_materials: job?.status_materials || 'red',
    status_accommodations: job?.status_accommodations || 'red',
    status_other: job?.status_other || 'red'
  });
  const [loading, setLoading] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'green': return 'bg-green-500';
      case 'yellow': return 'bg-yellow-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-red-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'green': return 'Complete';
      case 'yellow': return 'Called';
      case 'red': return 'Not Called';
      default: return 'Not Called';
    }
  };

  const handleStatusChange = (field, newStatus) => {
    setRequirements({
      ...requirements,
      [field]: newStatus
    });
  };

  const handleRequirementToggle = (field, checked) => {
    setRequirements({
      ...requirements,
      [field]: checked
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/jobs/${job.id}/requirements`, requirements);
      alert('✅ Requirements status updated successfully!');
      onUpdate(); // Refresh the jobs list
      onClose();
    } catch (error) {
      console.error('Error updating requirements:', error);
      if (error.response?.status === 404) {
        alert('❌ Job not found. It may have been deleted.');
      } else if (error.response?.status === 400) {
        alert('❌ Invalid data provided. Please check your inputs.');
      } else {
        alert('❌ Error updating requirements. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const requirementTypes = [
    {
      key: 'utility_locates',
      icon: '🚦',
      label: 'Utility Locates',
      description: 'Track locate calls before excavation'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto" data-testid="requirements-modal">
      <div className="bg-white rounded-xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-testid="requirements-modal-content">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">
          🚦 Utility Locates Status - {job?.customer_name}
        </h2>
        
        <div className="space-y-6">
          {requirementTypes.map((type) => {
            const requirementField = `requirements_${type.key}`;
            const statusField = `status_${type.key}`;
            const isEnabled = requirements[requirementField];
            const currentStatus = requirements[statusField];
            
            return (
              <div key={type.key} className="border border-gray-200 rounded-lg p-4">
                {/* Requirement Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) => handleRequirementToggle(requirementField, e.target.checked)}
                      className="w-5 h-5 text-yellow-600 rounded focus:ring-yellow-500"
                      data-testid={`requirements-toggle-${type.key}`}
                    />
                    <div>
                      <h3 className="font-semibold text-gray-800">
                        {type.icon} {type.label}
                      </h3>
                      <p className="text-sm text-gray-600">{type.description}</p>
                    </div>
                  </div>
                  
                  {isEnabled && (
                    <div className="flex items-center space-x-1">
                      <span className={`w-4 h-4 rounded-full ${getStatusColor(currentStatus)}`}></span>
                      <span className="text-sm font-medium text-gray-700">
                        {getStatusText(currentStatus)}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Status Controls */}
                {isEnabled && (
                  <div className="ml-8 space-y-3">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleStatusChange(statusField, 'red')}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${
                          currentStatus === 'red'
                            ? 'bg-red-100 border-red-300 text-red-800'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`requirements-status-${type.key}-red`}
                      >
                        <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                        <span className="text-sm font-medium">Not Called</span>
                      </button>
                      
                      <button
                        onClick={() => handleStatusChange(statusField, 'yellow')}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${
                          currentStatus === 'yellow'
                            ? 'bg-yellow-100 border-yellow-300 text-yellow-800'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`requirements-status-${type.key}-yellow`}
                      >
                        <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                        <span className="text-sm font-medium">Called</span>
                      </button>
                      
                      <button
                        onClick={() => handleStatusChange(statusField, 'green')}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${
                          currentStatus === 'green'
                            ? 'bg-green-100 border-green-300 text-green-800'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`requirements-status-${type.key}-green`}
                      >
                        <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                        <span className="text-sm font-medium">Complete</span>
                      </button>
                    </div>
                    
                    {/* Other Requirements Description */}
                    {type.key === 'other' && isEnabled && (
                      <input
                        type="text"
                        placeholder="Describe other requirements..."
                        value={requirements.requirements_other_description}
                        onChange={(e) => setRequirements({
                          ...requirements,
                          requirements_other_description: e.target.value
                        })}
                        className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        data-testid="requirements-other-description-input"
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-4 mt-8">
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 py-4 rounded-xl font-bold text-white text-lg"
            data-testid="requirements-save-button"
          >
            {loading ? 'Saving...' : '💾 Save Changes'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-500 hover:bg-gray-600 py-4 rounded-xl font-bold text-white text-lg"
            data-testid="requirements-cancel-button"
          >
            Cancel
          </button>
        </div>
        </div>
      </div>
  );
};

// Jobs Module Component
const JobsModule = ({ onBack, currentUser, jobToEdit, setJobToEdit, onJobEditComplete }) => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open'); // 'open' | 'shortlist' | 'completed'
  const [divisionFilter, setDivisionFilter] = useState('all'); // 'all' | 'Foundations' | 'Waterproofing' | 'Septics'
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewJobForm, setShowNewJobForm] = useState(false);
  const [showCalendarPreview, setShowCalendarPreview] = useState(false);
  const [calendarJobs, setCalendarJobs] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [showEditJobForm, setShowEditJobForm] = useState(false);
  const [queueSortAsc, setQueueSortAsc] = useState(true); // true = lowest->highest
  const [isMobileJobsView, setIsMobileJobsView] = useState(false);
  const [jobPendingDelete, setJobPendingDelete] = useState(null);
  const [cameFromCalendar, setCameFromCalendar] = useState(false);

  // Detect mobile viewport for JobsModule (used to disable drag on touch devices)
  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobileJobsView(window.innerWidth < 640);
      }
    };
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Mobile fit-to-width scaling for the Jobs table (no horizontal scrolling)
  const jobsFitRef = React.useRef(null);
  const jobsOuterRef = React.useRef(null);
  const [jobsMobileScale, setJobsMobileScale] = useState(1);

  useEffect(() => {
    const updateScale = () => {
      if (typeof window === 'undefined') return;
      if (window.innerWidth >= 640) {
        setJobsMobileScale(1);
        return;
      }

      const outer = jobsOuterRef.current;
      const el = jobsFitRef.current;
      if (!outer || !el) return;

      const contentWidth = el.scrollWidth || el.getBoundingClientRect().width || 1;
      const viewportWidth = outer.getBoundingClientRect().width || window.innerWidth;
      const scale = Math.min(1, viewportWidth / contentWidth);
      setJobsMobileScale(scale);
    };

    updateScale();
    const t = setTimeout(updateScale, 250);

    window.addEventListener('resize', updateScale);
    window.addEventListener('orientationchange', updateScale);

    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', updateScale);
      window.removeEventListener('orientationchange', updateScale);
    };
  }, [jobs.length, filteredJobs.length, statusFilter, divisionFilter, searchQuery]);


  const [editingJob, setEditingJob] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showForm, setShowForm] = useState(null); // 'commencement', 'completion', 'hazard', 'documents', 'photos'
  const [editingCompletionForm, setEditingCompletionForm] = useState(null);

  // Completion form history viewer
  const [completionFormsModal, setCompletionFormsModal] = useState(null); // job object
  const [completionFormsList, setCompletionFormsList] = useState([]);
  const [completionFormsLoading, setCompletionFormsLoading] = useState(false);

  const openCompletionForms = async (job) => {
    setCompletionFormsModal(job);
    setCompletionFormsList([]);
    setCompletionFormsLoading(true);
    try {
      const res = await axios.get(`${API}/forms/completion?job_id=${job.id}`);
      setCompletionFormsList(res.data.completion_forms || []);
    } catch (err) {
      console.error('Error fetching completion forms', err);
    }
    setCompletionFormsLoading(false);
  };

  const [completionPdfPreview, setCompletionPdfPreview] = useState(null);

  const downloadCompletionPdf = async (formId, customerName) => {
    try {
      const res = await axios.get(`${API}/forms/completion/${formId}/pdf`, {
        responseType: 'blob'
      });
      const blob = res.data;
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `Completion_${(customerName || 'form').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      console.error('PDF error:', err);
      alert('Failed to load PDF.');
    }
  };

  const handleEditCompletionForm = async (job, formSummary) => {
    try {
      const res = await axios.get(`${API}/forms/completion/${formSummary.id}`);
      setSelectedJob(job);
      setEditingCompletionForm(res.data || null);
      setCompletionFormsModal(null);
      setShowForm('completion');
    } catch (err) {
      console.error('Error loading completion form for edit', err);
      alert('Could not open this completion form for editing.');
    }
  };
  
  // Employees state for crew assignment
  const [employees, setEmployees] = useState([]);
  const [showCrewDropdown, setShowCrewDropdown] = useState(false);
  
  // PDF functionality states
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfJob, setPDFJob] = useState(null);
  const [pdfEmail, setPDFEmail] = useState('');
  const [pdfSending, setPDFSending] = useState(false);
  
  // Important Details functionality states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsJob, setDetailsJob] = useState(null);
  
  // Internal Admin Notes functionality states
  const [internalNotes, setInternalNotes] = useState([]);
  const [newInternalNote, setNewInternalNote] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesJob, setNotesJob] = useState(null);
  
  // Document Email functionality states
  const [showDocumentEmailModal, setShowDocumentEmailModal] = useState(false);
  const [documentEmailJob, setDocumentEmailJob] = useState(null);
  const [documentEmail, setDocumentEmail] = useState('');
  const [documentSending, setDocumentSending] = useState(false);

  // Photo upload functionality states
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoJob, setPhotoJob] = useState(null);
  const [photoType, setPhotoType] = useState('pre_work'); // 'pre_work', 'progress', 'completion'
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoDescriptions, setPhotoDescriptions] = useState({});
  const [photoUploading, setPhotoUploading] = useState(false);

  // Requirements Status Editor Modal states
  const [showRequirementsModal, setShowRequirementsModal] = useState(false);
  const [editingJobRequirements, setEditingJobRequirements] = useState(null);

  const [salesClients, setSalesClients] = useState([]);
  const [salesClientSearchNew, setSalesClientSearchNew] = useState('');
  const [salesClientSearchEdit, setSalesClientSearchEdit] = useState('');
  const [showSalesClientResultsNew, setShowSalesClientResultsNew] = useState(false);
  const [showSalesClientResultsEdit, setShowSalesClientResultsEdit] = useState(false);
  const [showSalesClientModal, setShowSalesClientModal] = useState(false);
  const [salesClientTarget, setSalesClientTarget] = useState('new');
  const [salesClientForm, setSalesClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: 'ON',
    postal_code: '',
    notes: ''
  });
  const [isSavingSalesClient, setIsSavingSalesClient] = useState(false);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [showLinkContactsModal, setShowLinkContactsModal] = useState(false);
  const [linkingContactsJob, setLinkingContactsJob] = useState(null);
  const [selectedLinkedContactIds, setSelectedLinkedContactIds] = useState([]);
  const [savingLinkedContacts, setSavingLinkedContacts] = useState(false);
  const [linkContactsSearch, setLinkContactsSearch] = useState('');

  const filterSalesClients = (query) => {
    const q = query.trim().toLowerCase();
    // Merge sales clients with unique job customers
    const seen = new Set();
    const combined = [];
    // Add sales clients first
    for (const client of salesClients) {
      const key = (client.email || client.name || '').toLowerCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        combined.push(client);
      }
    }
    // Add job customers not already in sales clients
    for (const job of jobs) {
      const name = job.customer_name || '';
      const email = (job.contact_email || '').toLowerCase();
      const key = email || name.toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      combined.push({
        id: `job-${job.id}`,
        name,
        email: job.contact_email || '',
        phone: job.contact_phone || '',
        address: job.job_location || '',
        city: '',
        province: '',
        postal_code: '',
      });
    }
    const matches = combined.filter((client) => {
      if (!q) return true;
      return [
        client.name,
        client.email,
        client.phone,
        client.address,
        client.city,
        client.province,
        client.postal_code
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(q));
    });
    return matches.slice(0, 12);
  };

  const filteredSalesClientsNew = filterSalesClients(salesClientSearchNew);
  const filteredSalesClientsEdit = filterSalesClients(salesClientSearchEdit);
  const canManageCompanyContacts = ['admin', 'manager'].includes(currentUser?.rawRole || '');

  // Jobs filter/tab states
  const [activeFilter, setActiveFilter] = useState('recent'); // legacy filters (kept for internal logic, UI simplified)
  const [openActionsJobId, setOpenActionsJobId] = useState(null);
  const actionsButtonRefs = React.useRef({});
  const actionsMenuRef = React.useRef(null);
  const [actionsMenuStyle, setActionsMenuStyle] = useState(null);


  const [newJob, setNewJob] = useState({
    customer_name: '',
    job_location: '',
    contact_email: '',
    contact_phone: '',
    job_details: '',
    po_number: '',
    estimated_duration: '',
    crew_size_needed: '',
    equipment_required: '',
    location_type: 'in_town',
    division: '',
    assigned_crew_members: [],
    is_shortlist: false,
    // Simple scheduling fields for internal calendar
    start_date: '',
    end_date: '',
    duration_days: '',
    // Backend will set queue_number and is_completed
  });

  const [editJob, setEditJob] = useState({
    customer_name: '',
    job_location: '',
    contact_email: '',
    contact_phone: '',
    job_details: '',
    po_number: '',
    estimated_duration: '',
    crew_size_needed: '',
    equipment_required: '',
    location_type: 'in_town',
    division: '',
    assigned_crew_members: [],
    assigned_employees: [], // for UI convenience when editing crew
    is_completed: false,
    is_shortlist: false,
  });

  useEffect(() => {
    fetchJobs();
    fetchEmployees();
    fetchCompanyContacts();
  }, []);

  useEffect(() => {
    if (currentUser?.rawRole === 'admin') {
      fetchSalesClients();
    }
  }, [currentUser?.rawRole]);

  // Fetch employees for crew assignment
  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees/active`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const fetchCompanyContacts = async () => {
    try {
      const response = await axios.get(`${API}/company-contacts`);
      setCompanyContacts(response.data || []);
    } catch (error) {
      console.error('Error fetching company contacts:', error);
      setCompanyContacts([]);
    }
  };

  const getLinkedContactsForJob = (job) => {
    const ids = job?.linked_contact_ids || [];
    if (!ids.length) return [];
    const contactMap = new Map((companyContacts || []).map((contact) => [contact.id, contact]));
    return ids.map((id) => contactMap.get(id)).filter(Boolean);
  };

  const fetchSalesClients = async () => {
    try {
      const response = await axios.get(`${API}/sales/clients`);
      setSalesClients(response.data || []);
    } catch (error) {
      console.error('Error fetching sales clients:', error);
      setSalesClients([]);
    }
  };

  const formatSalesClientLocation = (client) => {
    const cityLine = [client.city, client.province].filter(Boolean).join(', ');
    return [client.address, cityLine, client.postal_code].filter(Boolean).join(' ');
  };

  const shouldConfirmClientOverwrite = (jobData) => {
    const hasExisting = [
      jobData.customer_name,
      jobData.job_location,
      jobData.contact_email,
      jobData.contact_phone
    ].some((value) => value && value.trim() !== '');
    if (!hasExisting) return true;
    return window.confirm('Replace current job customer details with the selected sales client?');
  };

  const applySalesClientToJob = (client, target) => {
    const location = formatSalesClientLocation(client);
    if (target === 'edit') {
      if (!shouldConfirmClientOverwrite(editJob)) return;
      setEditJob((prev) => ({
        ...prev,
        customer_name: client.name || prev.customer_name,
        contact_email: client.email || prev.contact_email,
        contact_phone: client.phone || prev.contact_phone,
        job_location: location || prev.job_location
      }));
      setSalesClientSearchEdit(client.name || '');
      setShowSalesClientResultsEdit(false);
    } else {
      if (!shouldConfirmClientOverwrite(newJob)) return;
      setNewJob((prev) => ({
        ...prev,
        customer_name: client.name || prev.customer_name,
        contact_email: client.email || prev.contact_email,
        contact_phone: client.phone || prev.contact_phone,
        job_location: location || prev.job_location
      }));
      setSalesClientSearchNew(client.name || '');
      setShowSalesClientResultsNew(false);
    }
  };

  const openSalesClientModal = (target) => {
    setSalesClientTarget(target);
    setSalesClientForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      province: 'ON',
      postal_code: '',
      notes: ''
    });
    setShowSalesClientModal(true);
  };

  const handleSaveSalesClient = async () => {
    if (!salesClientForm.name.trim()) {
      alert('Client name is required.');
      return;
    }
    try {
      setIsSavingSalesClient(true);
      const response = await axios.post(`${API}/sales/clients`, salesClientForm);
      const created = response.data;
      setSalesClients((prev) => [created, ...prev]);
      setShowSalesClientModal(false);
      applySalesClientToJob(created, salesClientTarget);
    } catch (error) {
      console.error('Error creating sales client:', error);
      alert('Unable to create client.');
    } finally {
      setIsSavingSalesClient(false);
    }
  };

  // Fetch calendar data for preview
  const fetchCalendarData = async (year, month) => {
    try {
      setCalendarLoading(true);
      console.log(`📅 [PRODUCTION DEBUG] Fetching calendar data for ${year}/${month}`);
      console.log(`📅 [PRODUCTION DEBUG] API URL: ${API}/calendar/jobs/${year}/${month}`);
      
      const [jobsResponse, eventsResponse] = await Promise.all([
        axios.get(`${API}/calendar/jobs/${year}/${month}`),
        axios.get(`${API}/calendar/events/${year}/${month}`)
      ]);
      
      console.log(`📅 [PRODUCTION DEBUG] SUCCESS: Received ${jobsResponse.data?.length || 0} jobs and ${eventsResponse.data?.length || 0} events`);
      
      setCalendarJobs(jobsResponse.data || []);
      setCalendarEvents(eventsResponse.data || []);
      
      if (jobsResponse.data && jobsResponse.data.length > 0) {
        console.log(`📅 [PRODUCTION DEBUG] Job customers: ${jobsResponse.data.map(j => j.customer_name).join(', ')}`);
      }
      
    } catch (error) {
      console.error('❌ [PRODUCTION DEBUG] Calendar API Error:', error.message);
      if (error.response) {
        console.error('❌ [PRODUCTION DEBUG] Error Status:', error.response.status);
        console.error('❌ [PRODUCTION DEBUG] Error Data:', error.response.data);
      }
      setCalendarJobs([]);
      setCalendarEvents([]);
      alert(`Calendar Error: ${error.message}`);
    } finally {
      setCalendarLoading(false);
    }
  };

  // Handle calendar preview for both add and edit
  const handleShowCalendarPreview = async (isEdit = false) => {
    const sourceJob = isEdit ? editJob : newJob;

    // Use timezone-safe date parsing to open to the job's scheduled month
    let targetDate;
    if (sourceJob.start_date) {
      const [year, month, day] = sourceJob.start_date.split('-').map(num => parseInt(num));
      targetDate = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      targetDate = new Date(); // fallback to current month if no start date
    }

    console.log(`📅 Opening FULL calendar for ${isEdit ? 'EDIT' : 'NEW'} job preview:`, {
      targetDate,
      startDate: sourceJob.start_date,
      customer: sourceJob.customer_name,
      sourceJobId: sourceJob.id
    });

    // Instead of using a separate mini preview calendar with its own data,
    // we now reuse the main CalendarModule UI in a modal so that the exact
    // same calendar and data are shown.

    // Set up calendarJobs / calendarEvents for the calendar module
    try {
      const year = targetDate.getFullYear();
      const month = targetDate.getMonth() + 1;

      // Load just the primary month for performance; CalendarModule will
      // handle navigation & additional loads as the user navigates.
      const [jobsResponse, eventsResponse] = await Promise.all([
        axios.get(`${API}/calendar/jobs/${year}/${month}`),
        axios.get(`${API}/calendar/events/${year}/${month}`)
      ]);

      setCalendarJobs(jobsResponse.data || []);
      setCalendarEvents(eventsResponse.data || []);
    } catch (error) {
      console.error('❌ Error loading calendar data for preview:', error);
      setCalendarJobs([]);
      setCalendarEvents([]);
    }

    // Show the modal that will render the full CalendarModule
    setShowCalendarPreview(true);
  };

  // Helper function to calculate overall requirements status
  const getRequirementsStatus = (job) => {
    const requirements = [
      job.requirements_utility_locates ? job.status_utility_locates : null,
      job.requirements_permit ? job.status_permit : null,
      job.requirements_materials ? job.status_materials : null,
      job.requirements_accommodations ? job.status_accommodations : null,
      job.requirements_other ? job.status_other : null
    ].filter(status => status !== null);
    
    if (requirements.length === 0) return 'no_requirements';
    
    const greenCount = requirements.filter(status => status === 'green').length;
    const yellowCount = requirements.filter(status => status === 'yellow').length;
    const redCount = requirements.filter(status => status === 'red').length;
    
    if (redCount > 0) return 'not_ready';
    if (yellowCount > 0) return 'in_progress';
    return 'complete';
  };

  const updateActionsMenuPosition = React.useCallback(() => {
    if (!openActionsJobId) return;
    const btn = actionsButtonRefs.current[openActionsJobId];
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 176; // w-44
    const gap = 6;
    const menuHeight = actionsMenuRef.current?.getBoundingClientRect().height || 280;
    const viewportPadding = 8;
    const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
    const spaceAbove = rect.top - gap - viewportPadding;
    const openUp = spaceBelow < Math.min(menuHeight, 260) && spaceAbove > spaceBelow;
    const maxHeight = Math.max(140, openUp ? spaceAbove : spaceBelow);
    const left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8);

    setActionsMenuStyle({
      position: 'fixed',
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? (window.innerHeight - rect.top + gap) : undefined,
      left: Math.max(8, left),
      width: menuWidth,
      maxHeight,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
      WebkitOverflowScrolling: 'touch',
      touchAction: 'pan-y',
      zIndex: 9999,
    });
  }, [openActionsJobId]);

  useEffect(() => {
    if (!openActionsJobId) {
      setActionsMenuStyle(null);
      return;
    }
    updateActionsMenuPosition();
    const onReposition = () => updateActionsMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [openActionsJobId, updateActionsMenuPosition]);

  useEffect(() => {
    if (!openActionsJobId) return;
    const handleOutsideClick = (event) => {
      const menu = actionsMenuRef.current;
      const btn = actionsButtonRefs.current[openActionsJobId];
      if (menu?.contains(event.target) || btn?.contains(event.target)) return;
      setOpenActionsJobId(null);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [openActionsJobId]);

  const openRequirementsEditor = (job) => {
    setEditingJobRequirements(job);
    setShowRequirementsModal(true);
  };

  const getLocatesStatusMeta = (status) => {
    switch (status) {
      case 'green':
        return { label: 'Complete', textClass: 'text-green-700' };
      case 'yellow':
        return { label: 'Called', textClass: 'text-yellow-700' };
      case 'red':
      default:
        return { label: 'Not Called', textClass: 'text-red-700' };
    }
  };

  const canEditLocates = currentUser?.rawRole === 'admin';

  // Updated filtering and sorting logic
  useEffect(() => {
    let filtered = [...jobs];

    // Apply simple Open/Shortlist/Completed filter first
    if (statusFilter === 'open') {
      filtered = filtered.filter(job => !job.is_completed);
    } else if (statusFilter === 'shortlist') {
      // Non-completed jobs that are marked as shortlist
      filtered = filtered.filter(job => !job.is_completed && job.is_shortlist);
    } else if (statusFilter === 'completed') {
      filtered = filtered.filter(job => job.is_completed);
    }

    // Apply division filter
    if (divisionFilter !== 'all') {
      filtered = filtered.filter(job => (job.division || '') === divisionFilter);
    }

    // Apply new filter categories (legacy)
    // Note: Shortlist/Warranty are now also represented via statusFilter/is_shortlist where needed
    switch (activeFilter) {
      case 'needs_input':
        filtered = filtered.filter(job => {
          // Jobs that need input are those missing critical information
          if (job.job_status === 'Needs Input') return true;
          
          // Auto-detect incomplete jobs
          const missingStartDate = !job.start_date || job.start_date === '';
          const missingEndDate = !job.end_date || job.end_date === '';
          const missingCrewAssignment = !job.assigned_crew_members || job.assigned_crew_members.length === 0;
          const hasGenericStatus = !job.job_status || job.job_status === 'Pending' || job.job_status === '';
          
          // Job needs input if it's missing key scheduling information
          return missingStartDate || missingEndDate || (missingCrewAssignment && hasGenericStatus);
        });
        break;
      case 'requirements_complete':
        filtered = filtered.filter(job => getRequirementsStatus(job) === 'complete');
        break;
      case 'requirements_in_progress':
        filtered = filtered.filter(job => getRequirementsStatus(job) === 'in_progress');
        break;
      case 'requirements_not_ready':
        filtered = filtered.filter(job => getRequirementsStatus(job) === 'not_ready');
        break;
      case 'in_progress':
        filtered = filtered.filter(job => 
          job.job_status === 'In Progress' || 
          job.job_status === 'Active' || 
          job.job_status === 'Started'
        );
        break;
      case 'completed':
        filtered = filtered.filter(job => 
          job.job_status === 'Completed' || 
          job.job_status === 'Finished' ||
          job.job_status === 'Done'
        );
        break;
      case 'on_hold':
        filtered = filtered.filter(job => 
          job.job_status === 'On Hold' || 
          job.job_status === 'Paused' ||
          job.job_status === 'Delayed'
        );
        break;
      case 'shortlist':
        // Legacy shortlist filter kept for backwards compatibility with job_status
        filtered = filtered.filter(job => 
          job.job_status === 'Shortlist' || job.is_shortlist
        );
        break;
      case 'warranty':
        filtered = filtered.filter(job => 
          job.job_status === 'Warranty'
        );
        break;
      case 'coming_up':
        filtered = filtered.filter(job => {
          if (!job.start_date) return false;
          const startDate = new Date(job.start_date);
          const today = new Date();
          const thirtyDaysFromNow = new Date();
          thirtyDaysFromNow.setDate(today.getDate() + 30);
          return startDate >= today && startDate <= thirtyDaysFromNow;
        });
        // Sort by start date for coming up jobs
        filtered.sort((a, b) => {
          if (!a.start_date && !b.start_date) return 0;
          if (!a.start_date) return 1;
          if (!b.start_date) return -1;
          return new Date(a.start_date) - new Date(b.start_date);
        });
        break;
      case 'recent':
      default:
        // Sort by creation date (most recent first) for default view
        filtered.sort((a, b) => {
          const dateA = new Date(a.created_date || a.id); // Fallback to ID if no created_date
          const dateB = new Date(b.created_date || b.id);
          return dateB - dateA; // Newest first
        });
        break;
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(job => {
        // Search across multiple fields
        const searchFields = [
          job.customer_name || '',
          job.job_location || '',
          job.job_details || '',
          job.job_summary || '',
          job.important_notes || '',
          job.safety_precautions || '',
          job.special_requirements || '',
          job.priority_level || '',
          job.job_status || '',
          job.po_number || '',
          job.contact_email || '',
          job.contact_phone || '',
          job.equipment_required || '',
          job.site_access_notes || '',
          job.crew_size_needed || '',
          job.estimated_duration || ''
        ];
        
        return searchFields.some(field => 
          field.toLowerCase().includes(query)
        );
      });
    }

    // Final sort: queue number ascending/descending when available
    filtered.sort((a, b) => {
      const qa = typeof a.queue_number === 'number' ? a.queue_number : Number.MAX_SAFE_INTEGER;
      const qb = typeof b.queue_number === 'number' ? b.queue_number : Number.MAX_SAFE_INTEGER;
      return queueSortAsc ? qa - qb : qb - qa;
    });

    setFilteredJobs(filtered);
  }, [jobs, searchQuery, statusFilter, divisionFilter, activeFilter, queueSortAsc]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  // Calendar integration helper functions
  const calculateEndDate = (startDate, durationDays) => {
    if (!startDate || !durationDays) return '';

    const [year, month, day] = startDate.split('-').map(num => parseInt(num, 10));
    let remaining = parseInt(durationDays, 10);
    if (isNaN(remaining) || remaining <= 0) return startDate;

    // Normalize to local date and ensure we start counting on a weekday
    let current = new Date(year, month - 1, day);
    // If start falls on a weekend, move to next Monday
    while (current.getDay() === 0 || current.getDay() === 6) {
      current.setDate(current.getDate() + 1);
    }

    // We count working days (Mon–Fri) only; start day is day 1
    remaining -= 1; // we are already on the first working day

    while (remaining > 0) {
      current.setDate(current.getDate() + 1);
      const dow = current.getDay();
      if (dow !== 0 && dow !== 6) {
        remaining -= 1;
      }
    }

    const endYear = current.getFullYear();
    const endMonth = String(current.getMonth() + 1).padStart(2, '0');
    const endDay = String(current.getDate()).padStart(2, '0');
    return `${endYear}-${endMonth}-${endDay}`;
  };

  const calculateDurationDays = (startDate, endDate) => {
    if (!startDate || !endDate) return '';

    const [startYear, startMonth, startDay] = startDate.split('-').map(num => parseInt(num, 10));
    const [endYear, endMonth, endDay] = endDate.split('-').map(num => parseInt(num, 10));

    let start = new Date(startYear, startMonth - 1, startDay);
    let end = new Date(endYear, endMonth - 1, endDay);

    if (end < start) return '1';

    // Ensure we start on a weekday for counting purposes
    while (start.getDay() === 0 || start.getDay() === 6) {
      start.setDate(start.getDate() + 1);
    }

    let count = 0;
    let cursor = new Date(start);

    while (cursor <= end) {
      const dow = cursor.getDay();
      if (dow !== 0 && dow !== 6) {
        count += 1;
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    return count > 0 ? count.toString() : '1';
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    
    // Validate internal scheduling fields
    if (!newJob.start_date) {
      alert('Please select a Start Date for internal scheduling.');
      return;
    }

    // You can either provide duration_days or end_date (we'll derive the missing one)
    if (!newJob.duration_days && !newJob.end_date) {
      alert('Please enter either a Duration (Days) or an End Date.');
      return;
    }

    // Derive missing side of the schedule
    let start_date = newJob.start_date;
    let duration_days = newJob.duration_days;
    let end_date = newJob.end_date;

    if (duration_days && !end_date) {
      end_date = calculateEndDate(start_date, duration_days);
    } else if (!duration_days && end_date) {
      duration_days = calculateDurationDays(start_date, end_date);
    }

    if (!end_date) {
      alert('Please ensure a valid End Date is set.');
      return;
    }

    try {
      const jobData = {
        ...newJob,
        start_date,
        end_date,
        duration_days,
      };
      
      const response = await axios.post(`${API}/jobs`, jobData);
      
      setNewJob({ 
        customer_name: '', 
        job_location: '', 
        contact_email: '', 
        contact_phone: '', 
        job_details: '', 
        po_number: '',
        estimated_duration: '',
        crew_size_needed: '',
        equipment_required: '',
        location_type: 'in_town',
        division: '',
        assigned_crew_members: [],
        is_shortlist: false,
        start_date: '',
        end_date: '',
        duration_days: '',
      });
      setShowNewJobForm(false);
      setShowCrewDropdown(false);
      fetchJobs();
      alert('Job created successfully!');
    } catch (error) {
      console.error('Error creating job:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      let errorMessage = 'Failed to create job. ';
      if (error.response?.data?.detail) {
        errorMessage += error.response.data.detail;
      } else if (error.response?.status) {
        errorMessage += `Server responded with status ${error.response.status}`;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  const handleEditJob = (job, options = {}) => {
    if (options.fromCalendar) {
      setCameFromCalendar(true);
    }
    setShowDetailsModal(false);
    setDetailsJob(null);
    setEditingJob(job);
    setEditJob({
      customer_name: job.customer_name || '',
      job_location: job.job_location || '',
      contact_email: job.contact_email || '',
      contact_phone: job.contact_phone || '',
      job_details: job.job_details || '',
      po_number: job.po_number || '',
      estimated_duration: job.estimated_duration || '',
      crew_size_needed: job.crew_size_needed || '',
      equipment_required: job.equipment_required || '',
      location_type: job.location_type || 'in_town',
      division: job.division || '',
      assigned_crew_members: job.assigned_crew_members || [],
      assigned_employees: job.assigned_crew_members || [],
      start_date: job.start_date || '',
      end_date: job.end_date || '',
      duration_days: job.duration_days || '',
      is_completed: !!job.is_completed,
      is_shortlist: !!job.is_shortlist,
    });
    setShowEditJobForm(true);
  };

  // Auto-open Edit Job form when a job is passed in from the Calendar
  useEffect(() => {
    if (jobToEdit) {
      // Use the job object provided from the calendar (already has the fields we need)
      handleEditJob(jobToEdit, { fromCalendar: true });
      if (setJobToEdit) {
        setJobToEdit(null); // Clear after consuming so it doesn't reopen repeatedly
      }
    }
  }, [jobToEdit]);


  const handleDeleteJob = async (job) => {
    if (!job) return;

    try {
      console.log('Deleting job:', job.id);
      const response = await axios.delete(`${API}/jobs/${job.id}`);
      console.log('Job deletion successful:', response.data);

      // Refresh jobs list to remove deleted job
      await fetchJobs();

      alert(
        `✅ Job deleted successfully!` +
          `\n\nJob: ${response.data.customer_name || job.customer_name}` +
          `\nDeleted: Job record, ${response.data.deleted_documents || 0} documents, ${response.data.deleted_photos || 0} photos`
      );
    } catch (error) {
      console.error('Error deleting job:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;

      if (status === 404) {
        alert('❌ Job not found. It may have already been deleted.');
        await fetchJobs();
      } else if (status === 403) {
        alert('❌ Access denied. Only administrators can delete jobs.');
      } else if (status === 401) {
        alert('❌ Authentication error. Please log in again and try deleting the job.');
      } else {
        alert(
          `❌ Error deleting job.` +
            (status ? ` (Status ${status})` : '') +
            (detail ? `\nDetails: ${detail}` : '')
        );
      }
    }
  };

  const confirmDeleteJob = (job) => {
    setJobPendingDelete(job);
  };

  const handleUpdateJob = async (e) => {
    e.preventDefault();
    
    console.log('Job update started');
    console.log('Updating job ID:', editingJob.id);
    console.log('Update data:', editJob);
    
    try {
      // Prepare the data with proper data types
      const updateData = {
        ...editJob,
        // Send assigned_crew_members directly (no separate assigned_employees field in simplified model)
        assigned_crew_members: editJob.assigned_crew_members || [],
        // Convert numeric fields - empty strings to null
        estimated_cost: editJob.estimated_cost === '' || editJob.estimated_cost === null ? null : parseFloat(editJob.estimated_cost) || null,
        actual_cost: editJob.actual_cost === '' || editJob.actual_cost === null ? null : parseFloat(editJob.actual_cost) || null,
        profit_margin: editJob.profit_margin === '' || editJob.profit_margin === null ? null : parseFloat(editJob.profit_margin) || null,
        // Ensure duration_days is handled properly
        duration_days: editJob.duration_days === '' || editJob.duration_days === null ? null : parseInt(editJob.duration_days) || null
      };
      
      // Remove the assigned_employees field since we're using assigned_crew_members
      delete updateData.assigned_employees;
      
      console.log('Prepared update data:', updateData);
      
      const response = await axios.put(`${API}/jobs/${editingJob.id}`, updateData);
      
      // Update local jobs list with edited job
      setJobs(prev => prev.map(j => (j.id === response.data.id ? response.data : j)));
      console.log('Job update successful:', response.data);
      
      setEditJob({
        customer_name: '',
        job_location: '',
        contact_email: '',
        contact_phone: '',
        job_details: '',
        po_number: '',
        estimated_duration: '',
        crew_size_needed: '',
        equipment_required: '',
        start_date: '',
        duration_days: '',
        end_date: '',
        location_type: 'in_town',
        assigned_employees: [],
        is_shortlist: false,
        requirements_utility_locates: false,
        requirements_permit: false,
        requirements_materials: false,
        requirements_accommodations: false,
        requirements_other: false,
        requirements_other_description: '',
        status_utility_locates: 'red',
        status_permit: 'red',
        status_materials: 'red',
        status_accommodations: 'red',
        status_other: 'red'
      });
      setShowEditJobForm(false);
      setEditingJob(null);
      fetchJobs();

      // If this edit was triggered from the calendar, notify parent so it can return there
      if (cameFromCalendar && onJobEditComplete) {
        onJobEditComplete(response.data);
      }

      // Reset the flag after a successful update
      setCameFromCalendar(false);

      alert('Job updated successfully!');
    } catch (error) {
      console.error('Error updating job:', error);
      console.error('Error response:', error.response);
      console.error('Error details:', error.response?.data);
      
      let errorMessage = 'Please try again.';
      
      if (error.response?.status === 400) {
        // Handle validation errors (array of error objects)
        if (Array.isArray(error.response.data.detail)) {
          const validationErrors = error.response.data.detail
            .map(err => `${err.loc ? err.loc.join('.') : 'Field'}: ${err.msg}`)
            .join('\n');
          errorMessage = `Validation errors:\n${validationErrors}`;
        } else {
          errorMessage = `Invalid data: ${error.response.data.detail || 'Please check your input fields.'}`;
        }
      } else if (error.response?.status === 404) {
        errorMessage = 'Job not found. It may have been deleted.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You may not have permission to edit this job.';
      } else if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errors = error.response.data.detail
            .map(err => `${err.loc ? err.loc.join('.') : 'Error'}: ${err.msg}`)
            .join('\n');
          errorMessage = `Server errors:\n${errors}`;
        } else {
          errorMessage = `Server error: ${error.response.data.detail}`;
        }
      } else {
        errorMessage = error.message || 'Unknown error occurred.';
      }
      
      alert(`❌ Error updating job:\n\n${errorMessage}`);
    }
  };

  // PDF functionality handlers
  const handleSendPDF = (job) => {
    setPDFJob(job);
    setPDFEmail('');
    setShowPDFModal(true);
  };

  const handlePDFSubmit = async (e) => {
    e.preventDefault();
    setPDFSending(true);
    
    try {
      const response = await axios.post(`${API}/jobs/${pdfJob.id}/send-pdf`, {
        recipient_email: pdfEmail
      });
      
      alert(`PDF sent successfully to ${pdfEmail}!`);
      setShowPDFModal(false);
      setPDFJob(null);
      setPDFEmail('');
    } catch (error) {
      console.error('Error sending PDF:', error);
      alert('Error sending PDF. Please try again.');
    } finally {
      setPDFSending(false);
    }
  };

  // Important Details functionality handlers
  const handleViewDetails = (job) => {
    setDetailsJob(job);
    setShowDetailsModal(true);
  };

  const openLinkContactsModal = (job) => {
    setLinkingContactsJob(job);
    setSelectedLinkedContactIds(job?.linked_contact_ids || []);
    setLinkContactsSearch('');
    setShowLinkContactsModal(true);
  };

  const handleSaveLinkedContacts = async () => {
    if (!linkingContactsJob || !canManageCompanyContacts) return;
    setSavingLinkedContacts(true);
    try {
      const response = await axios.patch(`${API}/jobs/${linkingContactsJob.id}/contacts`, {
        contact_ids: selectedLinkedContactIds,
      });

      const updatedJob = response.data;
      setJobs((prev) => prev.map((job) => (job.id === updatedJob.id ? updatedJob : job)));
      setFilteredJobs((prev) => prev.map((job) => (job.id === updatedJob.id ? updatedJob : job)));
      setDetailsJob((prev) => (prev && prev.id === updatedJob.id ? updatedJob : prev));
      setSelectedJob((prev) => (prev && prev.id === updatedJob.id ? updatedJob : prev));
      setShowLinkContactsModal(false);
      setLinkingContactsJob(null);
    } catch (error) {
      console.error('Error updating linked contacts:', error);
      alert(error.response?.data?.detail || 'Failed to update job contacts');
    } finally {
      setSavingLinkedContacts(false);
    }
  };

  // Internal Notes functionality handlers
  const fetchInternalNotes = async (jobId) => {
    if (currentUser?.role !== 'Admin/Manager') return;
    
    setLoadingNotes(true);
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/internal-notes`);
      setInternalNotes(response.data.internal_notes || []);
    } catch (error) {
      console.error('Error fetching internal notes:', error);
      setInternalNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const [hasQueueChanges, setHasQueueChanges] = useState(false);
  const [isSavingQueueOrder, setIsSavingQueueOrder] = useState(false);
  const [queueSaveNotice, setQueueSaveNotice] = useState('');

  useEffect(() => {
    if (!queueSaveNotice) return;
    const timer = setTimeout(() => setQueueSaveNotice(''), 2600);
    return () => clearTimeout(timer);
  }, [queueSaveNotice]);

  const handleToggleCompleted = async (job) => {
    try {
      await axios.patch(`${API}/jobs/${job.id}/toggle-completed`);
      // Refresh from backend so queue_number values are always contiguous
      await fetchJobs();
    } catch (error) {
      console.error('Error toggling job completed status:', error);
      alert('Failed to update job status.');
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    // Only allow drag-reorder on Open jobs, with no search and default filter
    if (statusFilter !== 'open') return;
    if (searchQuery.trim()) return;
    if (activeFilter !== 'recent') return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (sourceIndex === destIndex) return;

    const openJobs = jobs.filter(job => !job.is_completed);
    if (openJobs.length === 0) return;

    // Map open jobs by id for quick lookup
    const openById = {};
    openJobs.forEach(j => {
      openById[j.id] = j;
    });

    const filteredOpenIds = filteredJobs
      .filter(job => !job.is_completed)
      .map(job => job.id);

    // Ensure we're working with the full open set
    if (filteredOpenIds.length !== openJobs.length) {
      return;
    }

    const reorderedIds = Array.from(filteredOpenIds);
    const [moved] = reorderedIds.splice(sourceIndex, 1);
    reorderedIds.splice(destIndex, 0, moved);

    // Rebuild open jobs with new queue_number sequence
    let queuePos = 1;
    const newOpenJobs = reorderedIds.map(id => ({
      ...openById[id],
      queue_number: queuePos++,
    }));

    const completedJobs = jobs.filter(job => job.is_completed);
    const newJobs = [...newOpenJobs, ...completedJobs];

    setJobs(newJobs);
    setHasQueueChanges(true);
  };

  const handleSaveQueueOrder = async () => {
    if (isSavingQueueOrder) return;
    setIsSavingQueueOrder(true);
    try {
      const openJobs = jobs
        .filter(job => !job.is_completed)
        .sort((a, b) => (a.queue_number || 0) - (b.queue_number || 0));

      await axios.post(`${API}/jobs/queue-order`, {
        ordered_job_ids: openJobs.map(job => job.id),
      });

      await fetchJobs();
      setHasQueueChanges(false);
      setQueueSaveNotice('saved');
    } catch (error) {
      console.error('Error saving queue order:', error);
      setQueueSaveNotice('error');
    } finally {
      setIsSavingQueueOrder(false);
    }
  };


  const handleViewNotes = (job) => {
    setNotesJob(job);
    setShowNotesModal(true);
    fetchInternalNotes(job.id);
  };

  const handleAddInternalNote = async (e) => {
    e.preventDefault();
    if (!newInternalNote.trim() || !notesJob) return;
    
    setAddingNote(true);
    try {
      const response = await axios.post(`${API}/jobs/${notesJob.id}/internal-notes`, {
        message: newInternalNote.trim()
      });
      
      // Add the new note to the beginning of the list
      setInternalNotes(prev => [response.data, ...prev]);
      setNewInternalNote('');
      
    } catch (error) {
      console.error('Error adding internal note:', error);
      alert('Error adding note. Please try again.');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteInternalNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this internal note?')) return;
    
    try {
      await axios.delete(`${API}/jobs/${notesJob.id}/internal-notes/${noteId}`);
      setInternalNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting internal note:', error);
      alert('Error deleting note. Please try again.');
    }
  };

  // Cleanup test documents function
  const handleCleanupTestDocuments = async (job) => {
    if (!window.confirm(`Are you sure you want to clean up test/placeholder documents from ${job.customer_name}'s job?\n\nThis will remove documents with names containing: test, purple, square, main, website, placeholder, or very small files.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/jobs/${job.id}/cleanup-test-documents`);
      const data = response.data;
      
      if (data.deleted_count > 0) {
        alert(`✅ SUCCESS: Cleaned up ${data.deleted_count} test/placeholder documents from ${job.customer_name}'s job.\n\nThese invalid documents will no longer be sent in emails.`);
      } else {
        alert(`ℹ️ INFO: No test/placeholder documents found for ${job.customer_name}'s job.\n\nAll documents appear to be valid.`);
      }
      
      // Refresh the jobs list
      await fetchJobs();
    } catch (error) {
      console.error('Error cleaning up test documents:', error);
      if (error.response?.status === 403) {
        alert('❌ ACCESS DENIED: Only administrators can clean up test documents.');
      } else {
        alert('❌ ERROR: Failed to clean up test documents. Please try again.');
      }
    }
  };
  const handleSendDocuments = (job) => {
    setDocumentEmailJob(job);
    setDocumentEmail('');
    setShowDocumentEmailModal(true);
  };

  const handleDocumentEmailSubmit = async (e) => {
    e.preventDefault();
    setDocumentSending(true);
    
    try {
      const response = await axios.post(`${API}/jobs/${documentEmailJob.id}/send-documents`, {
        recipient_email: documentEmail
      });
      
      // Show detailed feedback based on response
      const data = response.data;
      if (data.document_count > 0) {
        alert(`✅ SUCCESS: ${data.document_count} job documents sent successfully to ${documentEmail}!\n\nDocuments included job files, drawings, specifications, and permits for ${data.customer_name}.`);
      } else {
        alert(`📋 INFO: Job information summary sent to ${documentEmail}!\n\nSince no documents have been uploaded for this job yet, we sent a detailed job summary instead. Once documents are uploaded, they will be included in future emails.`);
      }
      
      setShowDocumentEmailModal(false);
      setDocumentEmailJob(null);
      setDocumentEmail('');
    } catch (error) {
      console.error('Error sending documents:', error);
      if (error.response?.status === 404) {
        alert('❌ ERROR: Job not found or no documents available.');
      } else if (error.response?.status === 500) {
        alert('❌ ERROR: Failed to process documents for email. Please try again or contact support.');
      } else {
        alert('❌ ERROR: Failed to send email. Please check the email address and try again.');
      }
    } finally {
      setDocumentSending(false);
    }
  };

  // Helper functions for priority and status colors and icons
  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'Urgent': return 'bg-red-600 text-white';
      case 'High': return 'bg-orange-600 text-white';
      case 'Normal': return 'bg-yellow-600 text-white';
      case 'Low': return 'bg-green-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Completed': return 'bg-green-600 text-white';
      case 'In Progress': return 'bg-blue-600 text-white';
      case 'On Hold': return 'bg-gray-600 text-white';
      case 'Scheduled': return 'bg-purple-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'Urgent': return '🔥';
      case 'High': return '⚠️';
      case 'Normal': return '📋';
      case 'Low': return '📝';
      default: return '📋';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Completed': return '✅';
      case 'In Progress': return '⚡';
      case 'On Hold': return '⏸️';
      case 'Scheduled': return '📅';
      default: return '📋';
    }
  };

  // Photo upload functionality handlers
  const handleUploadPhotos = (job, type = 'pre_work') => {
    setPhotoJob(job);
    setPhotoType(type);
    setSelectedPhotos([]);
    setPhotoDescriptions({});
    setShowPhotoModal(true);
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const photoPromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            file: file,
            preview: reader.result,
            base64: reader.result
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(photoPromises).then(photos => {
      setSelectedPhotos(prev => {
        const newPhotos = [...prev, ...photos];
        // Initialize descriptions for new photos
        const newDescriptions = {};
        photos.forEach((photo, index) => {
          const photoIndex = prev.length + index;
          newDescriptions[photoIndex] = '';
        });
        setPhotoDescriptions(prevDesc => ({ ...prevDesc, ...newDescriptions }));
        return newPhotos;
      });
    });
  };

  const handlePhotoUpload = async () => {
    if (selectedPhotos.length === 0) {
      alert('Please select at least one photo to upload');
      return;
    }

    setPhotoUploading(true);
    try {
      const photosToUpload = selectedPhotos.map((photo, index) => ({
        photo: photo.base64,
        description: photoDescriptions[index] || 'No description provided'
      }));

      await axios.post(`${API}/jobs/${photoJob.id}/photos/upload`, {
        photos: photosToUpload,
        photo_type: photoType,
        uploaded_by: 'Current User' // This could be dynamic based on logged in user
      });

      alert(`Successfully uploaded ${selectedPhotos.length} ${photoType.replace('_', ' ')} photos!`);
      setShowPhotoModal(false);
      setSelectedPhotos([]);
      setPhotoDescriptions({});
      fetchJobs(); // Refresh jobs to show updated photo counts
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Error uploading photos. Please try again.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const removePhoto = (index) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoDescriptions(prev => {
      const newDescriptions = { ...prev };
      delete newDescriptions[index];
      // Reindex remaining descriptions
      const reindexed = {};
      Object.entries(newDescriptions).forEach(([key, value], newIndex) => {
        if (parseInt(key) > index) {
          reindexed[parseInt(key) - 1] = value;
        } else {
          reindexed[key] = value;
        }
      });
      return reindexed;
    });
  };

  const filteredCompanyContacts = (companyContacts || []).filter((contact) => {
    const q = (linkContactsSearch || '').trim().toLowerCase();
    if (!q) return true;
    return [contact.name, contact.company_name, contact.title, contact.phone, contact.email, contact.notes]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(q));
  });

  if (showForm === 'commencement') {
    return (
      <CommencementForm
        job={selectedJob}
        onBack={() => setShowForm(null)}
        onSubmit={() => setShowForm(null)}
      />
    );
  }

  if (showForm === 'completion') {
    return (
      <CompletionForm
        job={selectedJob}
        existingForm={editingCompletionForm}
        onBack={() => {
          setEditingCompletionForm(null);
          setShowForm(null);
        }}
        onSubmit={() => {
          setEditingCompletionForm(null);
          setShowForm(null);
        }}
      />
    );
  }

  if (showForm === 'hazard') {
    return <HazardAssessmentForm job={selectedJob} onBack={() => setShowForm(null)} onSubmit={() => setShowForm(null)} />;
  }

  if (showForm === 'documents') {
    return <JobDocuments job={selectedJob} onBack={() => setShowForm(null)} />;
  }


  if (showForm === 'work-order') {
    return <JobWorkOrder job={selectedJob} onBack={() => setShowForm(null)} currentUser={currentUser} />;
  }

  if (showForm === 'photos') {
    return <JobPhotos job={selectedJob} onBack={() => setShowForm(null)} />;
  }

  if (showForm === 'phone-intake') {
    return (
      <PhoneIntakeModule
        onBack={() => setShowForm(null)}
        currentUser={currentUser}
        initialIntake={currentPhoneIntake}
      />
    );
  }

  if (showForm === 'lead-intake') {
    return (
      <LeadIntakeModule
        onBack={() => setShowForm(null)}
        currentUser={currentUser}
        initialIntake={currentPhoneIntake}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header - Mobile Responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4">
          <button onClick={onBack} className="text-gray-600 hover:text-gray-800 text-sm sm:text-base self-start flex items-center gap-1">
            <span className="text-lg">←</span>
            <span>Back to Production</span>
          </button>
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 text-center sm:text-left tracking-tight">Jobs</h1>
          {currentUser.permissions.canEdit ? (
            <button 
              onClick={() => setShowNewJobForm(true)}
              className="bg-orange-600 hover:bg-orange-700 px-4 py-2 sm:px-5 rounded-lg font-semibold text-white shadow-sm text-sm sm:text-base"
            >
              + New Job
            </button>
          ) : (
            <div className="text-gray-500 text-xs sm:text-sm px-4 py-3 sm:px-6 text-center">
              🔒 View Only Access
            </div>
          )}
        </div>

        {/* Universal Search Bar - Mobile Responsive */}
        <div className="mb-4 sm:mb-6">
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
              <svg className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="🔍 Search jobs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 sm:pl-12 pr-16 sm:pr-20 py-3 sm:py-4 rounded-xl bg-white border border-gray-300 text-gray-800 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-lg shadow-sm"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center"
              >
                <svg className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400 hover:text-gray-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          
          {/* Search Results Info */}
          <div className="text-center mt-2 sm:mt-3">
            {searchQuery ? (
              <p className="text-gray-400 text-xs sm:text-sm">
                {filteredJobs.length === 0 ? (
                  `No jobs found for "${searchQuery}"`
                ) : filteredJobs.length === 1 ? (
                  `Found 1 job for "${searchQuery}"`
                ) : (
                  `Found ${filteredJobs.length} jobs for "${searchQuery}"`
                )}
              </p>
            ) : (
              <p className="text-gray-400 text-xs sm:text-sm">
                Showing all {jobs.length} jobs
              </p>
            )}
          </div>

          {/* Filters: Status + Division */}
          <div className="mt-3 sm:mt-5 flex flex-wrap items-center justify-center gap-3">
            {/* Status dropdown */}
            <div className="flex items-center gap-2">
              <label className="text-xs sm:text-sm text-gray-600 font-medium">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="open">Open</option>
                <option value="shortlist">Shortlist</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Division dropdown */}
            <div className="flex items-center gap-2">
              <label className="text-xs sm:text-sm text-gray-600 font-medium">Division:</label>
              <select
                value={divisionFilter}
                onChange={(e) => setDivisionFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-xs sm:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All</option>
                <option value="Foundations">Foundations</option>
                <option value="Waterproofing">Waterproofing</option>
                <option value="Septics">Septics</option>
              </select>
            </div>
          </div>
        </div>

        {/* New Job Form Modal */}
        {showNewJobForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
            <div className="bg-white rounded-xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Create New Job</h2>
              <form onSubmit={handleCreateJob} className="space-y-6">
                {currentUser?.rawRole === 'admin' && (
                  <div className="mb-2" data-testid="job-sales-client-section">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-testid="job-sales-client-label">Sales Client Lookup</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search sales clients by name, email, phone, or address"
                        value={salesClientSearchNew}
                        onChange={(e) => {
                          setSalesClientSearchNew(e.target.value);
                          setShowSalesClientResultsNew(true);
                        }}
                        onFocus={() => setShowSalesClientResultsNew(true)}
                        className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="job-sales-client-search-new"
                      />
                      {showSalesClientResultsNew && (
                        <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="job-sales-client-results-new">
                          {filteredSalesClientsNew.length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-500" data-testid="job-sales-client-empty-new">No sales clients found.</div>
                          )}
                          {filteredSalesClientsNew.map((client) => (
                            <button
                              key={client.id}
                              type="button"
                              onClick={() => applySalesClientToJob(client, 'new')}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100"
                              data-testid={`job-sales-client-option-new-${client.id}`}
                            >
                              <div className="font-semibold text-gray-800">{client.name}</div>
                              <div className="text-xs text-gray-500">{client.email} • {client.phone}</div>
                              <div className="text-xs text-gray-500">{formatSalesClientLocation(client)}</div>
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => openSalesClientModal('new')}
                            className="w-full text-left px-4 py-3 text-blue-600 hover:bg-blue-50"
                            data-testid="job-sales-client-create-new"
                          >
                            + Create new client
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-2" data-testid="job-sales-client-help-new">
                      Selecting a sales client will populate the job name, contact details, and address.
                    </div>
                  </div>
                )}

                {/* Basic Information */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Basic Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Customer Name"
                      value={newJob.customer_name}
                      onChange={(e) => setNewJob({...newJob, customer_name: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Job Location"
                      value={newJob.job_location}
                      onChange={(e) => setNewJob({...newJob, job_location: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <input
                      type="email"
                      placeholder="Contact Email"
                      value={newJob.contact_email}
                      onChange={(e) => setNewJob({...newJob, contact_email: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <input
                      type="tel"
                      placeholder="Contact Phone"
                      value={newJob.contact_phone}
                      onChange={(e) => setNewJob({...newJob, contact_phone: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <input
                      type="text"
                      placeholder="PO Number"
                      value={newJob.po_number}
                      onChange={(e) => setNewJob({...newJob, po_number: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                {/* Job Details: Summary, notes & safety combined */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Job Details</h3>
                  <textarea
                    placeholder="Job Summary, details, and any important safety notes or special requirements"
                    value={newJob.job_details}
                    onChange={(e) => setNewJob({...newJob, job_details: e.target.value})}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-40"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Important notes for schedulers (e.g. MUST START ON THIS DATE, NEEDS IMMEDIATE ATTENTION)"
                    value={newJob.important_notes || ''}
                    onChange={(e) => setNewJob({ ...newJob, important_notes: e.target.value })}
                    className="mt-4 w-full p-3 rounded-lg border border-orange-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  />
                </div>

                {/* Resource Planning */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-blue-800 mb-4">Resource Planning</h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-blue-700 text-sm font-bold mb-2">Job Division</label>
                      <select
                        value={newJob.division || ''}
                        onChange={(e) => setNewJob({ ...newJob, division: e.target.value })}
                        className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select Division</option>
                        <option value="Foundations">Foundations</option>
                        <option value="Waterproofing">Waterproofing</option>
                        <option value="Septics">Septics</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={() => setNewJob({ ...newJob, is_shortlist: !newJob.is_shortlist })}
                      className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors ${
                        newJob.is_shortlist
                          ? 'bg-orange-50 border-orange-300 text-orange-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center justify-center w-6 h-6 rounded-full border text-xs font-bold ${
                            newJob.is_shortlist
                              ? 'bg-orange-500 border-orange-500 text-white'
                              : 'border-gray-300 text-gray-400'
                          }`}
                        >
                          SL
                        </span>
                        <span className="font-medium">Shortlist</span>
                      </span>
                      {newJob.is_shortlist && (
                        <span className="text-[11px] font-semibold uppercase tracking-wide">ON</span>
                      )}
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Crew Size Needed (e.g., 2-3 people)"
                      value={newJob.crew_size_needed}
                      onChange={(e) => setNewJob({ ...newJob, crew_size_needed: e.target.value })}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="mt-4">
                    <textarea
                      placeholder="Equipment Required (List specialized tools, machinery, vehicles needed)"
                      value={newJob.equipment_required}
                      onChange={(e) => setNewJob({...newJob, equipment_required: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24"
                    />
                    <textarea
                      placeholder="Site Access Notes (Parking, entry restrictions, access challenges)"
                      value={newJob.site_access_notes}
                      onChange={(e) => setNewJob({...newJob, site_access_notes: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24 mt-4"
                    />
                  </div>
                </div>

                {/* Location, Division & Calendar Preview */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-purple-800 mb-4">Location, Division & Schedule Preview</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-purple-700 text-sm font-bold mb-2">In Town or Out of Town</label>
                      <select
                        value={newJob.location_type}
                        onChange={(e) => setNewJob({ ...newJob, location_type: e.target.value })}
                        className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="in_town">In Town</option>
                        <option value="out_of_town">Out of Town</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-purple-700 text-sm font-bold mb-2">Job Division</label>
                      <select
                        value={newJob.division || ''}
                        onChange={(e) => setNewJob({ ...newJob, division: e.target.value })}
                        className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select Division</option>
                        <option value="Foundations">Foundations</option>
                        <option value="Waterproofing">Waterproofing</option>
                        <option value="Septics">Septics</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-4 grid md:grid-cols-3 gap-4">
                    {/* Start Date */}
                    <div>
                      <label className="block text-purple-700 text-sm font-bold mb-2">Start Date (Internal Schedule)</label>
                      <input
                        type="date"
                        value={newJob.start_date || ''}
                        onChange={(e) => {
                          const start_date = e.target.value;
                          let duration_days = newJob.duration_days;
                          let end_date = newJob.end_date;

                          if (duration_days && start_date) {
                            end_date = calculateEndDate(start_date, duration_days);
                          } else if (!duration_days && start_date && !end_date) {
                            end_date = start_date;
                          }

                          setNewJob({
                            ...newJob,
                            start_date,
                            end_date,
                          });
                        }}
                        className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    {/* Duration Days */}
                    <div>
                      <label className="block text-purple-700 text-sm font-bold mb-2">Duration (Days)</label>
                      <input
                        type="number"
                        min="1"
                        value={newJob.duration_days || ''}
                        onChange={(e) => {
                          const duration_days = e.target.value;
                          let end_date = newJob.end_date;
                          const start_date = newJob.start_date;

                          if (start_date && duration_days) {
                            end_date = calculateEndDate(start_date, duration_days);
                          }

                          setNewJob({
                            ...newJob,
                            duration_days,
                            end_date,
                          });
                        }}
                        className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    {/* End Date */}
                    <div>
                      <label className="block text-purple-700 text-sm font-bold mb-2">End Date</label>
                      <input
                        type="date"
                        value={newJob.end_date || ''}
                        onChange={(e) => {
                          const end_date = e.target.value;
                          const start_date = newJob.start_date;
                          let duration_days = newJob.duration_days;

                          if (start_date && end_date) {
                            duration_days = calculateDurationDays(start_date, end_date);
                          }

                          setNewJob({
                            ...newJob,
                            end_date,
                            duration_days,
                          });
                        }}
                        className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>

                  {newJob.start_date && newJob.end_date && (
                    <p className="mt-3 text-xs text-purple-700">
                      This job will appear on the internal calendar from <strong>{newJob.start_date}</strong> to <strong>{newJob.end_date}</strong>
                      {newJob.duration_days && ` (${newJob.duration_days} day${newJob.duration_days === '1' ? '' : 's'})`}.
                    </p>
                  )}

                </div>

                {/* Calendar Preview Button */}
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleShowCalendarPreview}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    📅 Preview Calendar Schedule
                  </button>
                  <p className="text-gray-600 text-sm mt-1">
                    View existing calendar to see where this job is likely to land in the queue.
                  </p>
                </div>

                {/* Crew Assignment */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-blue-800 mb-4">👥 Crew Assignment</h3>
                  <div className="space-y-4">
                    <p className="text-blue-700 text-sm">
                      Select crew members who will be assigned to this job. You can also assign crew members later through the calendar.
                    </p>
                    
                    {employees.length > 0 ? (
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-blue-800">
                          Select Crew Members:
                        </label>
                        
                        {/* Custom Dropdown for Employee Selection */}
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowCrewDropdown(!showCrewDropdown)}
                            className="w-full p-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex justify-between items-center hover:bg-gray-50"
                          >
                            <span className="text-gray-700">
                              {newJob.assigned_crew_members.length === 0 
                                ? "Click to select crew members" 
                                : `${newJob.assigned_crew_members.length} crew member${newJob.assigned_crew_members.length !== 1 ? 's' : ''} selected`
                              }
                            </span>
                            <svg className={`w-5 h-5 text-gray-400 transition-transform ${showCrewDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                          </button>
                          
                          {showCrewDropdown && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-blue-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {employees.map((employee) => (
                                <div
                                  key={employee.id}
                                  onClick={() => {
                                    const isSelected = newJob.assigned_crew_members.includes(employee.id);
                                    let updatedCrew;
                                    
                                    if (isSelected) {
                                      // Remove employee from selection
                                      updatedCrew = newJob.assigned_crew_members.filter(id => id !== employee.id);
                                    } else {
                                      // Add employee to selection
                                      updatedCrew = [...newJob.assigned_crew_members, employee.id];
                                    }
                                    
                                    setNewJob({
                                      ...newJob,
                                      assigned_crew_members: updatedCrew
                                    });
                                  }}
                                  className={`px-4 py-3 cursor-pointer hover:bg-blue-50 flex items-center justify-between border-b border-gray-100 last:border-b-0 ${
                                    newJob.assigned_crew_members.includes(employee.id) ? 'bg-blue-50' : ''
                                  }`}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                                      newJob.assigned_crew_members.includes(employee.id) 
                                        ? 'bg-blue-600 border-blue-600' 
                                        : 'border-gray-300'
                                    }`}>
                                      {newJob.assigned_crew_members.includes(employee.id) && (
                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                      )}
                                    </div>
                                    <span className="text-gray-700 font-medium">{employee.first_name} {employee.last_name}</span>
                                    <span className="text-gray-500 text-sm">- {employee.position || 'Crew Member'}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <p className="text-xs text-blue-600">
                          💡 Click to open the list and select individual employees
                        </p>
                      </div>
                    ) : (
                      <div className="text-center p-6 bg-white border border-blue-200 rounded-lg">
                        <p className="text-gray-500 mb-2">No employees found</p>
                        <p className="text-sm text-gray-400">Add employees in the Employees module to assign them to jobs</p>
                      </div>
                    )}
                    
                    {newJob.assigned_crew_members.length > 0 && (
                      <div className="p-3 bg-blue-100 border border-blue-200 rounded-lg">
                        <p className="text-blue-700 text-sm font-medium mb-2">
                          ✅ {newJob.assigned_crew_members.length} crew member{newJob.assigned_crew_members.length !== 1 ? 's' : ''} selected:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {newJob.assigned_crew_members.map((crewId) => {
                            const employee = employees.find(emp => emp.id === crewId);
                            return employee ? (
                              <span key={crewId} className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                                <span>{employee.first_name} {employee.last_name}</span>
                                <button
                                  onClick={() => {
                                    const updatedCrew = newJob.assigned_crew_members.filter(id => id !== crewId);
                                    setNewJob({
                                      ...newJob,
                                      assigned_crew_members: updatedCrew
                                    });
                                  }}
                                  className="ml-1 hover:bg-blue-700 rounded-full w-4 h-4 flex items-center justify-center text-xs"
                                  title="Remove from crew"
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Financial Information (hidden in simplified workflow) */}
                {false && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-green-800 mb-4">💰 Financial Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="number"
                      placeholder="Estimated Cost ($)"
                      value={newJob.estimated_cost}
                      onChange={(e) => setNewJob({...newJob, estimated_cost: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      step="0.01"
                      min="0"
                    />
                    <input
                      type="number"
                      placeholder="Actual Cost ($)"
                      value={newJob.actual_cost}
                      onChange={(e) => setNewJob({...newJob, actual_cost: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <select
                      value={newJob.billing_status}
                      onChange={(e) => setNewJob({...newJob, billing_status: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="not_billed">💸 Not Billed</option>
                      <option value="invoiced">📋 Invoiced</option>
                      <option value="paid">✅ Paid</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Profit Margin (%)"
                      value={newJob.profit_margin}
                      onChange={(e) => setNewJob({...newJob, profit_margin: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      step="0.01"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
                )}

                {/* Compliance Checklist (hidden / removed in simplified workflow) */}

                <div className="flex gap-4 pt-4">
                  <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold">
                    Create Job
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowNewJobForm(false);
                      setShowCrewDropdown(false);
                    }}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-bold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Sales Client Modal */}
        {showSalesClientModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto" data-testid="job-sales-client-modal">
            <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800" data-testid="job-sales-client-modal-title">Create New Sales Client</h3>
                <button
                  onClick={() => setShowSalesClientModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                  data-testid="job-sales-client-modal-close"
                >
                  ×
                </button>
              </div>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Client Name"
                  value={salesClientForm.name}
                  onChange={(e) => setSalesClientForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 rounded-lg border border-gray-300"
                  data-testid="job-sales-client-name"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={salesClientForm.email}
                  onChange={(e) => setSalesClientForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full p-3 rounded-lg border border-gray-300"
                  data-testid="job-sales-client-email"
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={salesClientForm.phone}
                  onChange={(e) => setSalesClientForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full p-3 rounded-lg border border-gray-300"
                  data-testid="job-sales-client-phone"
                />
                <input
                  type="text"
                  placeholder="Address"
                  value={salesClientForm.address}
                  onChange={(e) => setSalesClientForm(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full p-3 rounded-lg border border-gray-300"
                  data-testid="job-sales-client-address"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="City"
                    value={salesClientForm.city}
                    onChange={(e) => setSalesClientForm(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full p-3 rounded-lg border border-gray-300"
                    data-testid="job-sales-client-city"
                  />
                  <input
                    type="text"
                    placeholder="Province"
                    value={salesClientForm.province}
                    onChange={(e) => setSalesClientForm(prev => ({ ...prev, province: e.target.value }))}
                    className="w-full p-3 rounded-lg border border-gray-300"
                    data-testid="job-sales-client-province"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Postal Code"
                  value={salesClientForm.postal_code}
                  onChange={(e) => setSalesClientForm(prev => ({ ...prev, postal_code: e.target.value }))}
                  className="w-full p-3 rounded-lg border border-gray-300"
                  data-testid="job-sales-client-postal"
                />
                <textarea
                  placeholder="Notes (optional)"
                  value={salesClientForm.notes}
                  onChange={(e) => setSalesClientForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full p-3 rounded-lg border border-gray-300 h-24"
                  data-testid="job-sales-client-notes"
                />
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setShowSalesClientModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
                  data-testid="job-sales-client-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSalesClient}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
                  disabled={isSavingSalesClient}
                  data-testid="job-sales-client-save"
                >
                  {isSavingSalesClient ? 'Saving...' : 'Create Client'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Preview Modal */}
        {showCalendarPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col">
              {/* Header */}
              <div className="bg-gray-900 text-white px-6 py-4 flex justify-between items-center flex-shrink-0">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <span role="img" aria-label="calendar">📅</span>
                  Calendar Preview - Full Job Calendar
                </h2>
                <button
                  onClick={() => setShowCalendarPreview(false)}
                  className="text-gray-300 hover:text-white text-2xl font-bold"
                >
                  ×
                </button>
              </div>
  
              {/* Content: reuse the full CalendarModule so preview matches exactly */}
              <div className="flex-1 overflow-y-auto bg-slate-50">
                <CalendarModule
                  onBack={() => setShowCalendarPreview(false)}
                  currentUser={currentUser}
                  onEditJob={(job) => {
                    // If user chooses to edit a job from within preview, keep existing behavior
                    if (onJobEditComplete) {
                      onJobEditComplete(job);
                    }
                    setShowCalendarPreview(false);
                  }}
                  initialDate={(showEditJobForm && editJob.start_date) || newJob.start_date
                    ? (() => {
                        const src = showEditJobForm ? editJob.start_date : newJob.start_date;
                        const [y, m, d] = src.split('-').map(n => parseInt(n));
                        return new Date(y, m - 1, d);
                      })()
                    : new Date()}
                />
              </div>
            </div>
          </div>
        )}

        {/* Edit Job Form Modal */}
        {showEditJobForm && editingJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
            <div className="bg-white rounded-xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Edit Job - {editingJob.customer_name}</h2>
              {/* Edit Job is admin-only in simplified workflow */}
              {currentUser?.rawRole === 'admin' && (
              <form onSubmit={handleUpdateJob} className="space-y-6">
                {currentUser?.rawRole === 'admin' && (
                  <div className="mb-2" data-testid="job-sales-client-section-edit">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-testid="job-sales-client-label-edit">Sales Client Lookup</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search sales clients by name, email, phone, or address"
                        value={salesClientSearchEdit}
                        onChange={(e) => {
                          setSalesClientSearchEdit(e.target.value);
                          setShowSalesClientResultsEdit(true);
                        }}
                        onFocus={() => setShowSalesClientResultsEdit(true)}
                        className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="job-sales-client-search-edit"
                      />
                      {showSalesClientResultsEdit && (
                        <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="job-sales-client-results-edit">
                          {filteredSalesClientsEdit.length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-500" data-testid="job-sales-client-empty-edit">No sales clients found.</div>
                          )}
                          {filteredSalesClientsEdit.map((client) => (
                            <button
                              key={client.id}
                              type="button"
                              onClick={() => applySalesClientToJob(client, 'edit')}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100"
                              data-testid={`job-sales-client-option-edit-${client.id}`}
                            >
                              <div className="font-semibold text-gray-800">{client.name}</div>
                              <div className="text-xs text-gray-500">{client.email} • {client.phone}</div>
                              <div className="text-xs text-gray-500">{formatSalesClientLocation(client)}</div>
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => openSalesClientModal('edit')}
                            className="w-full text-left px-4 py-3 text-blue-600 hover:bg-blue-50"
                            data-testid="job-sales-client-create-edit"
                          >
                            + Create new client
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-2" data-testid="job-sales-client-help-edit">
                      Selecting a sales client will populate the job name, contact details, and address.
                    </div>
                  </div>
                )}
                
                {/* Basic Information */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Basic Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Customer Name"
                      value={editJob.customer_name}
                      onChange={(e) => setEditJob({...editJob, customer_name: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Job Location"
                      value={editJob.job_location}
                      onChange={(e) => setEditJob({...editJob, job_location: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <input
                      type="email"
                      placeholder="Contact Email"
                      value={editJob.contact_email}
                      onChange={(e) => setEditJob({...editJob, contact_email: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <input
                      type="tel"
                      placeholder="Contact Phone"
                      value={editJob.contact_phone}
                      onChange={(e) => setEditJob({...editJob, contact_phone: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <input
                      type="text"
                      placeholder="PO Number"
                      value={editJob.po_number}
                      onChange={(e) => setEditJob({...editJob, po_number: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                {/* Job Details: Summary, notes & safety combined */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Job Details</h3>
                  <textarea
                    placeholder="Job Summary, details, and any important safety notes or special requirements"
                    value={editJob.job_details}
                    onChange={(e) => setEditJob({...editJob, job_details: e.target.value})}
                    className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-40"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Important notes for schedulers (e.g. MUST START ON THIS DATE, NEEDS IMMEDIATE ATTENTION)"
                    value={editJob.important_notes || ''}
                    onChange={(e) => setEditJob({ ...editJob, important_notes: e.target.value })}
                    className="mt-4 w-full p-3 rounded-lg border border-orange-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  />
                </div>

                {/* Resource Planning */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-blue-800 mb-4">Resource Planning</h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-blue-700 text-sm font-bold mb-2">Job Division</label>
                      <select
                        value={editJob.division || ''}
                        onChange={(e) => setEditJob({ ...editJob, division: e.target.value })}
                        className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select Division</option>
                        <option value="Foundations">Foundations</option>
                        <option value="Waterproofing">Waterproofing</option>
                        <option value="Septics">Septics</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={() => setEditJob({ ...editJob, is_shortlist: !editJob.is_shortlist })}
                      className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors ${
                        editJob.is_shortlist
                          ? 'bg-orange-50 border-orange-300 text-orange-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center justify-center w-6 h-6 rounded-full border text-xs font-bold ${
                            editJob.is_shortlist
                              ? 'bg-orange-500 border-orange-500 text-white'
                              : 'border-gray-300 text-gray-400'
                          }`}
                        >
                          SL
                        </span>
                        <span className="font-medium">Shortlist</span>
                      </span>
                      {editJob.is_shortlist && (
                        <span className="text-[11px] font-semibold uppercase tracking-wide">ON</span>
                      )}
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Crew Size Needed (e.g., 2-3 people)"
                      value={editJob.crew_size_needed}
                      onChange={(e) => setEditJob({ ...editJob, crew_size_needed: e.target.value })}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="mt-4">
                    <textarea
                      placeholder="Equipment Required (List specialized tools, machinery, vehicles needed)"
                      value={editJob.equipment_required}
                      onChange={(e) => setEditJob({...editJob, equipment_required: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24"
                    />
                    <textarea
                      placeholder="Site Access Notes (Parking, entry restrictions, access challenges)"
                      value={editJob.site_access_notes}
                      onChange={(e) => setEditJob({...editJob, site_access_notes: e.target.value})}
                      className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24 mt-4"
                    />
                  </div>
                </div>

                {/* Crew Assignment */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-blue-800 mb-4">👥 Crew Assignment</h3>
                  <div className="space-y-4">
                    <p className="text-blue-700 text-sm">
                      Select crew members who will be assigned to this job. You can also assign crew members later through the calendar.
                    </p>
                    {employees.length > 0 ? (
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-blue-800">
                          Select Crew Members:
                        </label>
                        <div className="grid md:grid-cols-2 gap-2">
                          {employees.map((employee) => {
                            const isSelected = editJob.assigned_crew_members?.includes(employee.id);
                            return (
                              <button
                                key={employee.id}
                                type="button"
                                onClick={() => {
                                  let updatedCrew;
                                  if (isSelected) {
                                    updatedCrew = editJob.assigned_crew_members.filter(id => id !== employee.id);
                                  } else {
                                    updatedCrew = [...(editJob.assigned_crew_members || []), employee.id];
                                  }
                                  setEditJob({
                                    ...editJob,
                                    assigned_crew_members: updatedCrew,
                                  });
                                }}
                                className={`flex items-center justify-between px-3 py-2 rounded border text-sm transition-colors ${
                                  isSelected
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-800 border-blue-200 hover:bg-blue-50'
                                }`}
                              >
                                <span>
                                  {employee.first_name} {employee.last_name}
                                </span>
                                {isSelected && <span className="ml-2">✔</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center p-4 bg-white border border-blue-200 rounded-lg text-sm text-gray-500">
                        No employees found. Add employees in the Employees module first.
                      </div>
                    )}

                    {editJob.assigned_crew_members && editJob.assigned_crew_members.length > 0 && (
                      <div className="p-3 bg-blue-100 border border-blue-200 rounded-lg">
                        <p className="text-blue-700 text-sm font-medium mb-2">
                          ✅ {editJob.assigned_crew_members.length} crew member{editJob.assigned_crew_members.length !== 1 ? 's' : ''} selected:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {editJob.assigned_crew_members.map((crewId) => {
                            const employee = employees.find(emp => emp.id === crewId);
                            return employee ? (
                              <span
                                key={crewId}
                                className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1"
                              >
                                <span>
                                  {employee.first_name} {employee.last_name}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updatedCrew = editJob.assigned_crew_members.filter(id => id !== crewId);
                                    setEditJob({
                                      ...editJob,
                                      assigned_crew_members: updatedCrew,
                                    });
                                  }}
                                  className="ml-1 hover:bg-blue-700 rounded-full w-4 h-4 flex items-center justify-center text-xs"
                                  title="Remove from crew"
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Location & Calendar Preview */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-purple-800 mb-4">Location & Schedule Preview</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-purple-700 text-sm font-bold mb-2">Start Date</label>
                      <input
                        type="date"
                        value={editJob.start_date}
                        onChange={(e) => {
                          const startDate = e.target.value;
                          const endDate = editJob.duration_days 
                            ? calculateEndDate(startDate, editJob.duration_days)
                            : startDate;
                          setEditJob({...editJob, start_date: startDate, end_date: endDate});
                        }}
                        className="w-full p-4 rounded-lg border border-purple-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-purple-700 text-sm font-bold mb-2">Duration (Days)</label>
                      <input
                        type="number"
                        placeholder="Number of days"
                        value={editJob.duration_days}
                        onChange={(e) => {
                          const durationDays = e.target.value;
                          const endDate = editJob.start_date 
                            ? calculateEndDate(editJob.start_date, durationDays)
                            : '';
                          setEditJob({...editJob, duration_days: durationDays, end_date: endDate});
                        }}
                        className="w-full p-4 rounded-lg border border-purple-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-purple-700 text-sm font-bold mb-2">End Date</label>
                      <input
                        type="date"
                        value={editJob.end_date}
                        onChange={(e) => {
                          const endDate = e.target.value;
                          const durationDays = editJob.start_date 
                            ? calculateDurationDays(editJob.start_date, endDate)
                            : '';
                          setEditJob({...editJob, end_date: endDate, duration_days: durationDays});
                        }}
                        className="w-full p-4 rounded-lg border border-purple-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-purple-700 text-sm font-bold mb-2">Location Type</label>
                    <select
                      value={editJob.location_type}
                      onChange={(e) => setEditJob({...editJob, location_type: e.target.value})}
                      className="w-full p-4 rounded-lg border border-purple-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="in_town">🏠 In Town</option>
                      <option value="out_of_town">🚛 Out of Town</option>
                    </select>
                  </div>
                  {editJob.start_date && editJob.end_date && (
                    <div className="mt-4 p-3 bg-purple-100 border border-purple-200 rounded-lg">
                      <p className="text-purple-700 text-sm">
                        ℹ️ This job will appear in the calendar from{' '}
                        <strong>{editJob.start_date}</strong> to{' '}
                        <strong>{editJob.end_date}</strong>
                        {editJob.duration_days && ` (${editJob.duration_days} days)`}
                      </p>
                    </div>
                  )}
                  
                  {/* Calendar Preview Button for Edit Job */}
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => handleShowCalendarPreview(true)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      📅 Preview Calendar Schedule
                    </button>
                    <p className="text-gray-600 text-sm mt-1">
                      View existing jobs and events to adjust scheduling if needed
                    </p>
                  </div>
                </div>

                {/* Financial Information (hidden in simplified workflow) */}
                {false && (
                <div className="bg-gray-700 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-green-400 mb-4">💰 Financial Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="number"
                      placeholder="Estimated Cost ($)"
                      value={editJob.estimated_cost}
                      onChange={(e) => setEditJob({...editJob, estimated_cost: e.target.value})}
                      className="w-full p-4 rounded-lg bg-gray-600 text-white"
                      step="0.01"
                      min="0"
                    />
                    <input
                      type="number"
                      placeholder="Actual Cost ($)"
                      value={editJob.actual_cost}
                      onChange={(e) => setEditJob({...editJob, actual_cost: e.target.value})}
                      className="w-full p-4 rounded-lg bg-gray-600 text-white"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <select
                      value={editJob.billing_status}
                      onChange={(e) => setEditJob({...editJob, billing_status: e.target.value})}
                      className="w-full p-4 rounded-lg bg-gray-600 text-white"
                    >
                      <option value="not_billed">💸 Not Billed</option>
                      <option value="invoiced">📋 Invoiced</option>
                      <option value="paid">✅ Paid</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Profit Margin (%)"
                      value={editJob.profit_margin}
                      onChange={(e) => setEditJob({...editJob, profit_margin: e.target.value})}
                      className="w-full p-4 rounded-lg bg-gray-600 text-white"
                      step="0.01"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
                )}

                {/* Compliance Checklist (hidden / removed in simplified workflow) */}

                <div className="flex gap-4 pt-4">
                  <button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 py-3 rounded-lg font-bold">
                    Update Job
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowEditJobForm(false);
                      setEditingJob(null);
                      // If this edit originated from the calendar, return there instead of staying on Jobs
                      if (cameFromCalendar && onJobEditComplete) {
                        onJobEditComplete(null);
                      }
                      setCameFromCalendar(false);
                    }}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 py-3 rounded-lg font-bold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        )}

        {/* Send PDF Modal */}
        {showPDFModal && pdfJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
            <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">📄 Send Job PDF Report</h2>
              <form onSubmit={handlePDFSubmit} className="space-y-6">
                
                {/* Job Information */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6 border">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Job Details</h3>
                  <p className="text-gray-600">Customer: {pdfJob.customer_name}</p>
                  <p className="text-gray-600">Location: {pdfJob.job_location}</p>
                  <p className="text-gray-600">Priority: {pdfJob.priority_level || 'Normal'}</p>
                  <p className="text-gray-600">Status: {pdfJob.job_status || 'Scheduled'}</p>
                </div>

                {/* Email Input */}
                <div>
                  <label className="block text-gray-800 text-sm font-bold mb-2">
                    Send PDF Report To:
                  </label>
                  <input
                    type="email"
                    placeholder="Enter recipient email address"
                    value={pdfEmail}
                    onChange={(e) => setPDFEmail(e.target.value)}
                    className="w-full p-4 rounded-lg bg-white border border-gray-300 text-gray-800 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                {/* PDF Contents Info */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="text-blue-700 font-bold text-sm mb-2">📋 PDF Will Include:</h4>
                  <ul className="text-blue-600 text-sm space-y-1">
                    <li>• Complete job and customer information</li>
                    <li>• Job summary and detailed description</li>
                    <li>• Important notes and safety precautions</li>
                    <li>• Special requirements and resource planning</li>
                    <li>• Equipment requirements and site access notes</li>
                    <li>• Professional formatting with company branding</li>
                  </ul>
                </div>

                <div className="flex gap-4 pt-4">
                  <button 
                    type="submit" 
                    disabled={pdfSending}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white shadow-sm"
                  >
                    {pdfSending ? '📤 Sending...' : '📧 Send PDF'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowPDFModal(false);
                      setPDFJob(null);
                      setPDFEmail('');
                    }}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white shadow-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Job Summary Modal – one-page overview (no scroll) */}
        {showDetailsModal && detailsJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-xl w-full max-w-5xl mx-auto shadow-2xl flex flex-col" style={{ maxHeight: '95vh' }}>
              {/* Header */}
              <div className="flex justify-between items-center px-4 sm:px-6 py-3 border-b border-gray-200 shrink-0">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 truncate">
                  {detailsJob.customer_name}
                  {detailsJob.division && <span className="ml-2 text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{detailsJob.division}</span>}
                  {detailsJob.is_shortlist && <span className="ml-2 text-xs font-medium bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Shortlist</span>}
                </h2>
                <button
                  onClick={() => { setShowDetailsModal(false); setDetailsJob(null); }}
                  className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4"
                  data-testid="job-details-close"
                >
                  ✕
                </button>
              </div>

              {/* Body – dense grid */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 overflow-y-auto flex-1">
                {/* Row 1: Contact + Schedule side-by-side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  {/* Contact & Location */}
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <h4 className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">Contact & Location</h4>
                    <p className="text-sm text-gray-800 font-medium">{detailsJob.job_location || '—'}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-600">
                      {detailsJob.contact_email && <span>{detailsJob.contact_email}</span>}
                      {detailsJob.contact_phone && <span>{detailsJob.contact_phone}</span>}
                      {detailsJob.po_number && <span className="text-blue-600 font-medium">PO: {detailsJob.po_number}</span>}
                    </div>
                    {detailsJob.location_type && (
                      <span className={`inline-block mt-1.5 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${detailsJob.location_type === 'out_of_town' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                        {detailsJob.location_type === 'out_of_town' ? 'Out of Town' : 'In Town'}
                      </span>
                    )}

                    <div className="mt-2" data-testid="job-details-linked-contacts-section">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Linked Contacts</span>
                        {canManageCompanyContacts && (
                          <button
                            type="button"
                            onClick={() => openLinkContactsModal(detailsJob)}
                            className="text-[10px] px-2 py-0.5 rounded border border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                            data-testid="job-details-link-contacts-btn"
                          >
                            Manage
                          </button>
                        )}
                      </div>
                      {getLinkedContactsForJob(detailsJob).length === 0 ? (
                        <p className="text-xs text-gray-400 italic mt-1" data-testid="job-details-linked-contacts-empty">No linked contacts</p>
                      ) : (
                        <div className="mt-1.5 space-y-1" data-testid="job-details-linked-contacts-list">
                          {getLinkedContactsForJob(detailsJob).map((contact) => (
                            <div key={contact.id} className="rounded bg-cyan-50 border border-cyan-100 px-2 py-1" data-testid={`job-details-linked-contact-${contact.id}`}>
                              <p className="text-xs font-medium text-cyan-800">{contact.name} {contact.title ? `• ${contact.title}` : ''}</p>
                              <p className="text-[11px] text-cyan-700">{contact.company_name}</p>
                              <div className="flex flex-wrap gap-2 mt-0.5 text-[11px] text-cyan-700">
                                {contact.phone ? <a href={`tel:${contact.phone}`} className="underline" data-testid={`job-details-linked-contact-phone-${contact.id}`}>{contact.phone}</a> : null}
                                {contact.email ? <a href={`mailto:${contact.email}`} className="underline" data-testid={`job-details-linked-contact-email-${contact.id}`}>{contact.email}</a> : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Schedule & Duration */}
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <h4 className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">Schedule</h4>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">Start</span>
                        <p className="text-gray-800 font-medium">{detailsJob.start_date || '—'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">End</span>
                        <p className="text-gray-800 font-medium">{detailsJob.end_date || '—'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Duration</span>
                        <p className="text-gray-800 font-medium">{detailsJob.duration_days ? `${detailsJob.duration_days} days` : detailsJob.estimated_duration || '—'}</p>
                      </div>
                    </div>
                    {detailsJob.assigned_crew_members && detailsJob.assigned_crew_members.length > 0 && (
                      <div className="mt-2">
                        <span className="text-gray-500 text-[10px] uppercase">Crew ({detailsJob.assigned_crew_members.length})</span>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {detailsJob.assigned_crew_members.map((crewId) => {
                            const emp = employees.find(e => e.id === crewId);
                            return emp ? (
                              <span key={crewId} className="bg-blue-100 text-blue-700 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                                {emp.first_name} {emp.last_name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 2: Job Details */}
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 mb-3">
                  <h4 className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Job Details</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{detailsJob.job_details || '—'}</p>
                </div>

                {/* Row 3: Notes, Safety, Requirements – compact 3-col on desktop */}
                {(detailsJob.important_notes || detailsJob.job_summary || detailsJob.safety_precautions || detailsJob.special_requirements) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                    {detailsJob.job_summary && (
                      <div className="bg-blue-50 rounded-lg p-2.5 border border-blue-200">
                        <h4 className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold mb-0.5">Summary</h4>
                        <p className="text-xs text-blue-700">{detailsJob.job_summary}</p>
                      </div>
                    )}
                    {detailsJob.important_notes && (
                      <div className="bg-orange-50 rounded-lg p-2.5 border border-orange-200">
                        <h4 className="text-[10px] uppercase tracking-wide text-orange-600 font-semibold mb-0.5">Important Notes</h4>
                        <p className="text-xs text-orange-700 font-medium">{detailsJob.important_notes}</p>
                      </div>
                    )}
                    {detailsJob.safety_precautions && (
                      <div className="bg-red-50 rounded-lg p-2.5 border border-red-200">
                        <h4 className="text-[10px] uppercase tracking-wide text-red-600 font-semibold mb-0.5">Safety</h4>
                        <p className="text-xs text-red-700 font-medium">{detailsJob.safety_precautions}</p>
                      </div>
                    )}
                    {detailsJob.special_requirements && (
                      <div className="bg-purple-50 rounded-lg p-2.5 border border-purple-200">
                        <h4 className="text-[10px] uppercase tracking-wide text-purple-600 font-semibold mb-0.5">Special Requirements</h4>
                        <p className="text-xs text-purple-700">{detailsJob.special_requirements}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Row 4: Resources + Requirements Status side-by-side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Resource Planning */}
                  {(detailsJob.crew_size_needed || detailsJob.equipment_required || detailsJob.site_access_notes) && (
                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                      <h4 className="text-[11px] uppercase tracking-wide text-green-600 font-semibold mb-1.5">Resources</h4>
                      <div className="space-y-1 text-xs">
                        {detailsJob.crew_size_needed && (
                          <p className="text-green-700"><span className="font-semibold">Crew:</span> {detailsJob.crew_size_needed}</p>
                        )}
                        {detailsJob.equipment_required && (
                          <p className="text-green-700"><span className="font-semibold">Equipment:</span> {detailsJob.equipment_required}</p>
                        )}
                        {detailsJob.site_access_notes && (
                          <p className="text-green-700"><span className="font-semibold">Site Access:</span> {detailsJob.site_access_notes}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Requirements Status */}
                  {(detailsJob.requirements_utility_locates || detailsJob.requirements_permit || detailsJob.requirements_materials || detailsJob.requirements_accommodations || detailsJob.requirements_other) && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <h4 className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">Requirements Status</h4>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {detailsJob.requirements_utility_locates && (
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${detailsJob.status_utility_locates === 'green' ? 'bg-green-500' : detailsJob.status_utility_locates === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                            <span className="text-gray-700">Utility Locates</span>
                          </div>
                        )}
                        {detailsJob.requirements_permit && (
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${detailsJob.status_permit === 'green' ? 'bg-green-500' : detailsJob.status_permit === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                            <span className="text-gray-700">Permit</span>
                          </div>
                        )}
                        {detailsJob.requirements_materials && (
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${detailsJob.status_materials === 'green' ? 'bg-green-500' : detailsJob.status_materials === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                            <span className="text-gray-700">Materials</span>
                          </div>
                        )}
                        {detailsJob.requirements_accommodations && (
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${detailsJob.status_accommodations === 'green' ? 'bg-green-500' : detailsJob.status_accommodations === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                            <span className="text-gray-700">Accommodations</span>
                          </div>
                        )}
                        {detailsJob.requirements_other && (
                          <div className="flex items-center gap-1.5 col-span-2">
                            <span className={`w-2 h-2 rounded-full ${detailsJob.status_other === 'green' ? 'bg-green-500' : detailsJob.status_other === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                            <span className="text-gray-700">Other: {detailsJob.requirements_other_description || '—'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 sm:px-6 py-3 border-t border-gray-200 shrink-0 text-right">
                <button
                  onClick={() => { setShowDetailsModal(false); setDetailsJob(null); }}
                  className="bg-gray-600 hover:bg-gray-700 py-2 px-6 rounded-lg font-semibold text-white text-sm"
                  data-testid="job-details-close-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showLinkContactsModal && linkingContactsJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[65] p-4" data-testid="job-link-contacts-modal-overlay">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl" data-testid="job-link-contacts-modal">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800" data-testid="job-link-contacts-modal-title">Link Contacts to Job</h3>
                  <p className="text-xs text-gray-500" data-testid="job-link-contacts-job-name">{linkingContactsJob.customer_name} • {linkingContactsJob.job_location || 'No location'}</p>
                </div>
                <button
                  onClick={() => {
                    setShowLinkContactsModal(false);
                    setLinkingContactsJob(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                  data-testid="job-link-contacts-close-btn"
                >
                  ✕
                </button>
              </div>

              <div className="px-5 py-4">
                <input
                  value={linkContactsSearch}
                  onChange={(e) => setLinkContactsSearch(e.target.value)}
                  placeholder="Search contacts"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="job-link-contacts-search-input"
                />

                <div className="mt-3 border border-gray-200 rounded-lg max-h-80 overflow-y-auto" data-testid="job-link-contacts-list">
                  {filteredCompanyContacts.length === 0 ? (
                    <p className="text-sm text-gray-500 px-3 py-4" data-testid="job-link-contacts-empty">No contacts found.</p>
                  ) : (
                    filteredCompanyContacts.map((contact) => {
                      const checked = selectedLinkedContactIds.includes(contact.id);
                      return (
                        <label key={contact.id} className="flex items-start gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0 cursor-pointer" data-testid={`job-link-contact-option-${contact.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setSelectedLinkedContactIds((prev) => {
                                if (e.target.checked) {
                                  return [...prev, contact.id];
                                }
                                return prev.filter((id) => id !== contact.id);
                              });
                            }}
                            className="mt-1"
                            data-testid={`job-link-contact-checkbox-${contact.id}`}
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-800" data-testid={`job-link-contact-name-${contact.id}`}>{contact.name}</p>
                            <p className="text-xs text-gray-600" data-testid={`job-link-contact-company-${contact.id}`}>{contact.company_name}{contact.title ? ` • ${contact.title}` : ''}</p>
                            <p className="text-xs text-gray-500" data-testid={`job-link-contact-channels-${contact.id}`}>{contact.phone || 'No phone'} {contact.email ? `• ${contact.email}` : ''}</p>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500" data-testid="job-link-contacts-selected-count">{selectedLinkedContactIds.length} selected</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setShowLinkContactsModal(false);
                      setLinkingContactsJob(null);
                    }}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-100"
                    data-testid="job-link-contacts-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveLinkedContacts}
                    disabled={savingLinkedContacts}
                    className="px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm hover:bg-cyan-700 disabled:opacity-50"
                    data-testid="job-link-contacts-save-btn"
                  >
                    {savingLinkedContacts ? 'Saving...' : 'Save Contacts'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Document Email Modal */}
        {showDocumentEmailModal && documentEmailJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4 overflow-y-auto">
            <div className="bg-white rounded-xl p-4 sm:p-6 lg:p-8 max-w-md w-full shadow-2xl my-8 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-800">📧 Email Job Documents</h2>
              <form onSubmit={handleDocumentEmailSubmit} className="space-y-4 sm:space-y-6">
                
                {/* Job Information */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 border">
                  <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2">Job Details</h3>
                  <p className="text-sm sm:text-base text-gray-600">Customer: {documentEmailJob.customer_name}</p>
                  <p className="text-sm sm:text-base text-gray-600">Location: {documentEmailJob.job_location}</p>
                  {documentEmailJob.po_number && <p className="text-sm sm:text-base text-gray-600">PO: {documentEmailJob.po_number}</p>}
                </div>

                {/* Email Input */}
                <div>
                  <label className="block text-gray-800 text-sm font-bold mb-2">
                    Send Documents To:
                  </label>
                  <input
                    type="email"
                    placeholder="Enter recipient email address"
                    value={documentEmail}
                    onChange={(e) => setDocumentEmail(e.target.value)}
                    className="w-full p-3 sm:p-4 rounded-lg bg-white border border-gray-300 text-gray-800 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    required
                  />
                </div>

                {/* Document Info */}
                <div className="bg-cyan-50 rounded-lg p-3 sm:p-4 border border-cyan-200">
                  <h4 className="text-cyan-700 font-bold text-sm mb-2">📁 What Will Be Sent:</h4>
                  <ul className="text-cyan-600 text-xs sm:text-sm space-y-1">
                    <li>• <strong>Valid job documents only:</strong> Real project files as attachments</li>
                    <li>• <strong>Automatic filtering:</strong> Test/placeholder documents excluded</li>
                    <li>• <strong>If no valid documents:</strong> Detailed job information summary</li>
                    <li>• Job details: customer info, location, PO number, crew assignment</li>
                    <li>• Professional email formatting with company branding</li>
                  </ul>
                  <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                    <p className="text-green-700 text-xs font-medium">
                      ✅ <strong>Smart Filtering:</strong> Only obvious test documents (like "test.pdf", "purple_square.jpg", "test_document.pdf") are filtered out. Legitimate documents with normal names are always sent.
                    </p>
                  </div>
                  <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <p className="text-blue-700 text-xs font-medium">
                      💡 <strong>Note:</strong> This email will contain actual job-specific information for <strong>{documentEmailJob.customer_name}</strong>, not generic website content.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                  <button 
                    type="submit" 
                    disabled={documentSending}
                    className="w-full sm:flex-1 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white shadow-sm text-sm sm:text-base"
                  >
                    {documentSending ? '📤 Sending...' : '📧 Send Documents'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowDocumentEmailModal(false);
                      setDocumentEmailJob(null);
                      setDocumentEmail('');
                    }}
                    className="w-full sm:flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white shadow-sm text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Photo Upload Modal */}
        {showPhotoModal && photoJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
            <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">📸 Upload Job Photos</h2>
              
              {/* Job Information */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 border">
                <h3 className="text-lg font-bold text-gray-800 mb-2">Job Details</h3>
                <p className="text-gray-600">Customer: {photoJob.customer_name}</p>
                <p className="text-gray-600">Location: {photoJob.job_location}</p>
                <p className="text-gray-600">Status: {photoJob.job_status || 'Scheduled'}</p>
              </div>

              {/* Photo Type Selection */}
              <div className="mb-6">
                <label className="block text-gray-800 text-sm font-bold mb-2">
                  Photo Type:
                </label>
                <select
                  value={photoType}
                  onChange={(e) => setPhotoType(e.target.value)}
                  className="w-full p-4 rounded-lg bg-white border border-gray-300 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="pre_work">Pre-Work Photos (Site Conditions, Existing Damage)</option>
                  <option value="progress">Progress Photos (Work in Progress)</option>
                  <option value="completion">Completion Photos (Finished Work)</option>
                </select>
              </div>

              {/* Photo Upload */}
              <div className="mb-6">
                <label className="block text-gray-800 text-sm font-bold mb-2">
                  Select Photos:
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="w-full p-4 rounded-lg bg-white border border-gray-300 text-gray-800 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <p className="text-gray-500 text-sm mt-2">Select multiple images to upload. Supports JPG, PNG, and other image formats.</p>
              </div>

              {/* Photo Previews */}
              {selectedPhotos.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-800 mb-4">Selected Photos ({selectedPhotos.length})</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {selectedPhotos.map((photo, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border">
                        <img 
                          src={photo.preview} 
                          alt={`Preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg mb-3"
                        />
                        <input
                          type="text"
                          placeholder="Photo description (required)"
                          value={photoDescriptions[index] || ''}
                          onChange={(e) => setPhotoDescriptions(prev => ({ ...prev, [index]: e.target.value }))}
                          className="w-full p-2 rounded border border-gray-300 text-sm"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          🗑️ Remove Photo
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Photo Type Info */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200 mb-6">
                <h4 className="text-green-700 font-bold text-sm mb-2">📸 Photo Documentation Best Practices:</h4>
                <ul className="text-green-600 text-sm space-y-1">
                  <li><strong>Pre-Work:</strong> Document existing site conditions, property damage, landscaping</li>
                  <li><strong>Progress:</strong> Capture work phases, crew activities, equipment usage</li>
                  <li><strong>Completion:</strong> Final results, clean-up, restored areas</li>
                  <li>• Add detailed descriptions for each photo for future reference</li>
                  <li>• Include timestamps and GPS data when possible</li>
                </ul>
              </div>

              <div className="flex gap-4 pt-4">
                <button 
                  type="button"
                  onClick={handlePhotoUpload}
                  disabled={photoUploading || selectedPhotos.length === 0}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white shadow-sm"
                >
                  {photoUploading ? '📤 Uploading...' : `📸 Upload ${selectedPhotos.length} Photos`}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowPhotoModal(false);
                    setPhotoJob(null);
                    setSelectedPhotos([]);
                    setPhotoDescriptions({});
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white shadow-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Requirements Status Modal */}
        {showRequirementsModal && editingJobRequirements && (
          <RequirementsEditorModal
            job={editingJobRequirements}
            onClose={() => {
              setShowRequirementsModal(false);
              setEditingJobRequirements(null);
            }}
            onUpdate={() => {
              fetchJobs();
            }}
          />
        )}

        {/* Completion Forms History Modal */}
        {completionFormsModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setCompletionFormsModal(null)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Submitted Completion Forms</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{completionFormsModal.customer_name} — {completionFormsModal.address}</p>
                </div>
                <button onClick={() => setCompletionFormsModal(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              <div className="overflow-y-auto flex-1 p-5 space-y-3">
                {completionFormsLoading ? (
                  <p className="text-sm text-gray-500 text-center py-8">Loading...</p>
                ) : completionFormsList.length === 0 ? (
                  <div className="text-center py-10">
                    <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm text-gray-500">No completion forms submitted yet for this job.</p>
                  </div>
                ) : completionFormsList.map((form) => (
                  <div key={form.id} className="border border-gray-100 rounded-lg p-4 bg-gray-50 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{form.customer_name}</p>
                      <p className="text-xs text-gray-500">{form.property_address}</p>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                        <span><span className="font-medium">Date:</span> {form.date_of_completion}</span>
                        <span><span className="font-medium">Submitted by:</span> {form.user_email}</span>
                        <span><span className="font-medium">Satisfaction:</span> {form.overall_satisfaction}</span>
                      </div>
                    </div>
                    <div className="shrink-0 flex flex-col gap-2" data-testid={`completion-form-card-actions-${form.id}`}>
                      <button
                        onClick={() => handleEditCompletionForm(completionFormsModal, form)}
                        className="flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                        data-testid={`edit-completion-form-${form.id}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => downloadCompletionPdf(form.id, form.customer_name)}
                        className="flex items-center justify-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                        data-testid={`download-completion-pdf-${form.id}`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        PDF
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Internal Admin Notes Modal */}
        {showNotesModal && notesJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
            <div className="bg-white rounded-xl p-8 max-w-4xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-green-800">🔒 Internal Admin Notes</h2>
                <button 
                  onClick={() => {
                    setShowNotesModal(false);
                    setNotesJob(null);
                    setInternalNotes([]);
                    setNewInternalNote('');
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>
              
              {/* Job Header */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{notesJob.customer_name}</h3>
                <p className="text-gray-600 mb-1">📍 {notesJob.job_location}</p>
                <p className="text-gray-600 mb-1">📅 {notesJob.start_date}</p>
                <p className="text-gray-600">📊 Status: <span className="font-semibold">{notesJob.job_status}</span></p>
              </div>

              {/* Internal Notes Section */}
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-green-700 text-sm mb-4">
                  Private notes visible only to admin users for internal communication and job tracking.
                </p>
                
                {/* Add New Note Form */}
                <form onSubmit={handleAddInternalNote} className="mb-6">
                  <div className="flex gap-2">
                    <textarea
                      value={newInternalNote}
                      onChange={(e) => setNewInternalNote(e.target.value)}
                      placeholder="Add an internal note (materials ordered, customer contacted, etc.)..."
                      className="flex-1 p-3 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                      rows="3"
                    />
                    <button
                      type="submit"
                      disabled={!newInternalNote.trim() || addingNote}
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
                    >
                      {addingNote ? 'Adding...' : 'Add Note'}
                    </button>
                  </div>
                </form>

                {/* Notes List */}
                <div className="space-y-4">
                  {loadingNotes ? (
                    <div className="text-center text-green-600 py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2"></div>
                      Loading notes...
                    </div>
                  ) : internalNotes.length === 0 ? (
                    <div className="text-center text-gray-500 py-8 italic">
                      <div className="text-4xl mb-2">📝</div>
                      No internal notes yet. Add the first note above to start tracking internal communications for this job.
                    </div>
                  ) : (
                    <>
                      <h4 className="font-bold text-green-800 text-lg mb-3">
                        📋 Notes History ({internalNotes.length})
                      </h4>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {internalNotes.map((note) => (
                          <div key={note.id} className="bg-white p-4 rounded-lg border border-green-200 shadow-sm">
                            <div className="flex justify-between items-start mb-3">
                              <div className="text-sm text-green-700">
                                <span className="font-bold text-green-800">{note.author_name}</span>
                                <span className="text-green-600 ml-2">
                                  {new Date(note.timestamp).toLocaleDateString()} at {new Date(note.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                              </div>
                              <button
                                onClick={() => handleDeleteInternalNote(note.id)}
                                className="text-red-500 hover:text-red-700 text-sm ml-2 px-2 py-1 rounded hover:bg-red-50"
                                title="Delete note"
                              >
                                ✕
                              </button>
                            </div>
                            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{note.message}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="text-center pt-4">
                <button 
                  onClick={() => {
                    setShowNotesModal(false);
                    setNotesJob(null);
                    setInternalNotes([]);
                    setNewInternalNote('');
                  }}
                  className="bg-gray-500 hover:bg-gray-600 py-3 px-8 rounded-lg font-bold text-white shadow-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Queue Save Floating Prompt */}
        {statusFilter === 'open' && !searchQuery.trim() && activeFilter === 'recent' && hasQueueChanges && (
          <div
            className="fixed bottom-5 left-1/2 z-40 w-[min(92vw,540px)] -translate-x-1/2 rounded-xl border border-amber-300 bg-amber-50/95 px-4 py-3 shadow-xl backdrop-blur"
            data-testid="jobs-queue-save-popup"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-amber-900" data-testid="jobs-queue-save-popup-message">
                Queue changed. Save the new order so everyone sees the same priority.
              </p>
              <div className="flex items-center gap-2" data-testid="jobs-queue-save-popup-actions">
                <button
                  type="button"
                  onClick={async () => {
                    await fetchJobs();
                    setHasQueueChanges(false);
                  }}
                  className="rounded border border-amber-300 px-2.5 py-1.5 text-[11px] font-medium text-amber-900 hover:bg-amber-100"
                  data-testid="jobs-queue-save-popup-reset-btn"
                  disabled={isSavingQueueOrder}
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSaveQueueOrder}
                  className="rounded bg-amber-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="jobs-queue-save-popup-save-btn"
                  disabled={isSavingQueueOrder}
                >
                  {isSavingQueueOrder ? 'Saving…' : 'Save new order'}
                </button>
              </div>
            </div>
          </div>
        )}

        {queueSaveNotice === 'saved' && (
          <div
            className="fixed right-4 top-20 z-40 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs font-semibold text-green-700 shadow"
            data-testid="jobs-queue-save-success-toast"
          >
            New order saved.
          </div>
        )}

        {queueSaveNotice === 'error' && (
          <div
            className="fixed right-4 top-20 z-40 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 shadow"
            data-testid="jobs-queue-save-error-toast"
          >
            Failed to save queue order.
          </div>
        )}



        {/* Jobs Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mt-4 overflow-x-hidden">
          <div ref={jobsOuterRef}
            style={
              isMobileJobsView && jobsMobileScale < 1
                ? { width: `${Math.round(100 / jobsMobileScale)}%`, transform: `scale(${jobsMobileScale})`, transformOrigin: 'top left' }
                : undefined
            }
          >
            <div ref={jobsFitRef}>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-900 text-gray-100">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide cursor-pointer select-none"
                      onClick={() => setQueueSortAsc(prev => !prev)}
                    >
                    Queue {queueSortAsc ? '▲' : '▼'}
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">Customer Info</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">Location</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">Utility Locates</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">Est Start Date & Duration</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">Notes</th>
                  <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="jobs-queue" direction="vertical">
                  {(provided) => (
                    <tbody
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="divide-y divide-gray-200"
                    >
                      {filteredJobs.map((job, index) => {
                        const highlightSearchText = (text, query) => {
                          if (!query || !text) return text;
                          const lowerText = text.toLowerCase();
                          const lowerQuery = query.toLowerCase();
                          let result = '';
                          let currentIndex = 0;
                          let matchIndex = lowerText.indexOf(lowerQuery);
                          if (matchIndex === -1) return text;
                          while (matchIndex !== -1) {
                            result += text.slice(currentIndex, matchIndex);
                            const matchText = text.slice(matchIndex, matchIndex + query.length);
                            result += `<mark class="bg-yellow-200 text-black px-0.5 rounded">${matchText}</mark>`;
                            currentIndex = matchIndex + query.length;
                            matchIndex = lowerText.indexOf(lowerQuery, currentIndex);
                          }
                          result += text.slice(currentIndex);
                          return result;
                        };

                        return (
                          <Draggable
                            key={job.id}
                            draggableId={job.id}
                            index={index}
                            isDragDisabled={
                              isMobileJobsView ||
                              statusFilter !== 'open' ||
                              !!searchQuery.trim() ||
                              activeFilter !== 'recent' ||
                              job.is_completed
                            }
                          >
                            {(dragProvided, snapshot) => (
                              <tr
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                onClick={() => handleViewDetails(job)}
                                className={`hover:bg-gray-50 cursor-pointer ${snapshot.isDragging ? 'bg-amber-50' : ''}`}
                              >
                                {/* Queue # */}
                                <td className="px-4 py-3 align-top text-xs text-gray-700 whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    {statusFilter === 'open' &&
                                      !searchQuery.trim() &&
                                      activeFilter === 'recent' &&
                                      !job.is_completed && (
                                        <span
                                          {...dragProvided.dragHandleProps}
                                          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 select-none"
                                          title="Drag to reorder in queue"
                                        >
                                          ⋮⋮
                                        </span>
                                      )}
                                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white border border-gray-400 text-[11px] font-semibold">
                                      {job.queue_number || index + 1}
                                    </span>
                                  </div>
                                </td>
                                {/* Customer Info */}
                                <td className="px-4 py-3 align-top max-w-xs">
                                  <div className="flex items-center gap-1">
                                    <div
                                      className="font-semibold text-gray-900 text-sm"
                                      dangerouslySetInnerHTML={{
                                        __html: highlightSearchText(
                                          job.customer_name || 'Untitled Job',
                                          searchQuery
                                        ),
                                      }}
                                    />
                                    {job.is_shortlist && !job.is_completed && (
                                      <span
                                        className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-orange-400 text-[9px] font-bold text-orange-500 ml-1"
                                        title="Shortlist"
                                      >
                                        SL
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-1 text-[11px] text-gray-600">
                                    {job.contact_phone || 'No phone on file'}
                                  </div>
                                  {job.po_number && (
                                    <div className="mt-1 text-[11px] text-blue-700 font-medium">
                                      PO:{' '}
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: highlightSearchText(job.po_number, searchQuery),
                                        }}
                                      />
                                    </div>
                                  )}
                                </td>

                                {/* Location */}
                                <td className="px-4 py-3 align-top max-w-xs">
                                  <div
                                    className="text-xs text-gray-900 font-medium"
                                    dangerouslySetInnerHTML={{
                                      __html: highlightSearchText(
                                        job.job_location || '—',
                                        searchQuery
                                      ),
                                    }}
                                  />
                                  <div className="mt-1 text-[11px] text-gray-500">
                                    {job.location_type === 'out_of_town'
                                      ? 'Out of town'
                                      : 'In town'}
                                  </div>
                                </td>

                                {/* Utility Locates Stoplight */}
                                <td className="px-4 py-3 align-top text-xs text-gray-800">
                                  {job.requirements_utility_locates ? (() => {
                                    const status = job.status_utility_locates || 'red';
                                    const meta = getLocatesStatusMeta(status);
                                    const indicator = (
                                      <>
                                        <span className="flex items-center gap-1.5 rounded-full bg-gray-900 px-2 py-1 shadow-inner">
                                          <span className={`${status === 'red' ? 'h-4 w-4 bg-red-500 ring-2 ring-red-300 shadow-[0_0_6px_rgba(239,68,68,0.6)]' : 'h-2.5 w-2.5 bg-red-200'} rounded-full`} />
                                          <span className={`${status === 'yellow' ? 'h-4 w-4 bg-yellow-400 ring-2 ring-yellow-200 shadow-[0_0_6px_rgba(250,204,21,0.6)]' : 'h-2.5 w-2.5 bg-yellow-200'} rounded-full`} />
                                          <span className={`${status === 'green' ? 'h-4 w-4 bg-green-500 ring-2 ring-green-200 shadow-[0_0_6px_rgba(34,197,94,0.6)]' : 'h-2.5 w-2.5 bg-green-200'} rounded-full`} />
                                        </span>
                                        <span className="flex flex-col items-start leading-tight">
                                          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-700">Locates</span>
                                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                                            status === 'green'
                                              ? 'bg-green-100 text-green-800'
                                              : status === 'yellow'
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-red-100 text-red-800'
                                          }`}
                                          >
                                            {meta.label}
                                          </span>
                                        </span>
                                      </>
                                    );

                                    return canEditLocates ? (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openRequirementsEditor(job);
                                        }}
                                        className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 hover:bg-gray-50"
                                        title={`Utility Locates: ${meta.label}`}
                                        data-testid={`locates-stoplight-${job.id}`}
                                      >
                                        {indicator}
                                      </button>
                                    ) : (
                                      <div
                                        className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1"
                                        title={`Utility Locates: ${meta.label}`}
                                        data-testid={`locates-stoplight-${job.id}`}
                                      >
                                        {indicator}
                                      </div>
                                    );
                                  })() : (
                                    <span className="text-[11px] text-gray-400 italic" data-testid={`locates-stoplight-none-${job.id}`}>—</span>
                                  )}
                                </td>

                                {/* Est Start Date & Duration */}
                                <td className="px-4 py-3 align-top text-xs text-gray-800">
                                  {job.start_date ? (
                                    <div className="flex flex-col">
                                      <span className="whitespace-nowrap">{job.start_date}</span>
                                      <span className="text-[11px] text-gray-500 mt-0.5">
                                        {job.duration_days ? `${job.duration_days} day${job.duration_days === 1 || job.duration_days === '1' ? '' : 's'}` : 'Duration not set'}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 italic">Not set</span>
                                  )}
                                </td>

                                {/* Notes */}
                                <td className="px-4 py-3 align-top text-xs text-gray-800 max-w-[160px]">
                                  {job.important_notes ? (
                                    <span className="inline-block max-w-full truncate" title={job.important_notes}>
                                      {job.important_notes}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 italic">—</span>
                                  )}
                                </td>

                                {/* Actions & Division Indicator */}
                                <td className="px-4 py-3 align-top text-right text-xs">
                                  {/* Actions dropdown (all screen sizes) */}
                                  <div className="flex flex-col items-end gap-1">
                                    <div className="relative inline-flex items-center gap-2">
                                      {/* Division indicator dot next to Actions */}
                                      {job.division && (
                                        <span
                                          className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold mr-1
                                            ${job.division === 'Foundations' ? 'bg-red-500 text-white'
                                              : job.division === 'Waterproofing' ? 'bg-blue-500 text-white'
                                              : job.division === 'Septics' ? 'bg-yellow-300 text-gray-900'
                                              : 'bg-gray-300 text-gray-800'}
                                          `}
                                          title={job.division}
                                        >
                                          {job.division === 'Foundations'
                                            ? 'F'
                                            : job.division === 'Waterproofing'
                                              ? 'W'
                                              : job.division === 'Septics'
                                                ? 'S'
                                                : 'O'}
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        ref={(el) => {
                                          actionsButtonRefs.current[job.id] = el;
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenActionsJobId(prev => (prev === job.id ? null : job.id));
                                        }}
                                        className="inline-flex items-center rounded border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                                        data-testid={`jobs-actions-button-${job.id}`}
                                      >
                                        Actions
                                        <span className="ml-1 text-[10px]">▾</span>
                                      </button>

                                      {openActionsJobId === job.id && createPortal(
                                        <div
                                          ref={actionsMenuRef}
                                          style={actionsMenuStyle || { position: 'fixed', top: 0, left: -9999 }}
                                          className="rounded-md border border-gray-200 bg-white shadow-lg text-left py-1 overscroll-contain"
                                          data-testid={`jobs-actions-menu-${job.id}`}
                                        >
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewDetails(job);
                                              setOpenActionsJobId(null);
                                            }}
                                            className="block w-full px-3 py-1.5 text-[11px] text-gray-800 hover:bg-gray-100 text-left"
                                            data-testid={`jobs-action-details-${job.id}`}
                                          >
                                            Details
                                          </button>
                                          {canManageCompanyContacts && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openLinkContactsModal(job);
                                                setOpenActionsJobId(null);
                                              }}
                                              className="block w-full px-3 py-1.5 text-[11px] text-gray-800 hover:bg-gray-100 text-left"
                                              data-testid={`jobs-action-link-contacts-${job.id}`}
                                            >
                                              Link Contacts
                                            </button>
                                          )}
                                          {currentUser?.rawRole === 'admin' && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openRequirementsEditor(job);
                                                setOpenActionsJobId(null);
                                              }}
                                              className="block w-full px-3 py-1.5 text-[11px] text-gray-800 hover:bg-gray-100 text-left"
                                              data-testid={`requirements-action-${job.id}`}
                                            >
                                              Requirements
                                            </button>
                                          )}
                                          {currentUser?.rawRole === 'admin' && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditJob(job);
                                                setOpenActionsJobId(null);
                                              }}
                                              className="block w-full px-3 py-1.5 text-[11px] text-gray-800 hover:bg-gray-100 text-left"
                                            >
                                              Edit job
                                            </button>
                                          )}
                                          <div className="border-t border-gray-100 my-1" />
                                          <button
                                            onClick={() => {
                                              setSelectedJob(job);
                                              setShowForm('commencement');
                                              setOpenActionsJobId(null);
                                            }}
                                            className="block w-full px-3 py-1.5 text-[11px] text-gray-700 hover:bg-gray-100 text-left"
                                            data-testid={`jobs-action-open-commencement-form-${job.id}`}
                                          >
                                            Start / Edit form
                                          </button>
                                          {currentUser.permissions.canEdit && !job.is_completed && (
                                            <button
                                              onClick={() => {
                                                handleToggleCompleted(job);
                                                setOpenActionsJobId(null);
                                              }}
                                              className="block w-full px-3 py-1.5 text-[11px] text-gray-700 hover:bg-gray-100 text-left"
                                            >
                                              Move to Completed
                                            </button>
                                          )}
                                          {currentUser.permissions.canEdit && job.is_completed && (
                                            <button
                                              onClick={() => {
                                                handleToggleCompleted(job);
                                                setOpenActionsJobId(null);
                                              }}
                                              className="block w-full px-3 py-1.5 text-[11px] text-gray-700 hover:bg-gray-100 text-left"
                                            >
                                              Move back to Open
                                            </button>
                                          )}
                                          <button
                                            onClick={() => {
                                              setSelectedJob(job);
                                              setEditingCompletionForm(null);
                                              setShowForm('completion');
                                              setOpenActionsJobId(null);
                                            }}
                                            className="block w-full px-3 py-1.5 text-[11px] text-gray-700 hover:bg-gray-100 text-left"
                                          >
                                            Completion form
                                          </button>
                                          <button
                                            onClick={() => {
                                              openCompletionForms(job);
                                              setOpenActionsJobId(null);
                                            }}
                                            className="block w-full px-3 py-1.5 text-[11px] text-green-700 hover:bg-green-50 text-left font-medium"
                                            data-testid={`view-completion-forms-${job.id}`}
                                          >
                                            View submitted forms
                                          </button>
                                          <button
                                            onClick={() => {
                                              setSelectedJob(job);
                                              setShowForm('documents');
                                              setOpenActionsJobId(null);
                                            }}
                                            className="block w-full px-3 py-1.5 text-[11px] text-gray-700 hover:bg-gray-100 text-left"
                                          >
                                            Files
                                          </button>
                                          <button
                                            onClick={() => {
                                              setSelectedJob(job);
                                              setShowForm('photos');
                                              setOpenActionsJobId(null);
                                            }}
                                            className="block w-full px-3 py-1.5 text-[11px] text-gray-700 hover:bg-gray-100 text-left"
                                          >
                                            Photos
                                          </button>
                                          {currentUser?.rawRole === 'admin' ? (
                                            <>
                                              <div className="border-t border-gray-100 my-1" />
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleSendPDF(job);
                                                  setOpenActionsJobId(null);
                                                }}
                                                className="block w-full px-3 py-1.5 text-[11px] text-gray-800 hover:bg-gray-100 text-left"
                                              >
                                                Send PDF
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  confirmDeleteJob(job);
                                                  setOpenActionsJobId(null);
                                                }}
                                                className="block w-full px-3 py-1.5 text-[11px] text-red-700 hover:bg-red-50 text-left"
                                              >
                                                Delete job
                                              </button>
                                            </>
                                          ) : null}
                                        </div>,
                                        document.body
                                      )}
                                    </div>
                                  </div>

                                </td>
                              </tr>
                            )}
                          </Draggable>
                        );
                      })}

                      {jobs.length === 0 && !searchQuery && (
                        <tr>
                          <td
                            colSpan="7"
                            className="px-4 py-8 text-center text-sm text-gray-400"
                          >
                            No jobs created yet. Use the "NEW JOB" button above to get started.
                          </td>
                        </tr>
                      )}

                      {jobs.length > 0 && filteredJobs.length === 0 && searchQuery && (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-400">
                            <div className="mb-2 text-lg">No jobs found for "{searchQuery}"</div>
                            <div className="text-xs mb-3">Try searching by customer, location, PO number, priority or status.</div>
                            <button
                              onClick={clearSearch}
                              className="inline-flex items-center rounded bg-gray-900 px-4 py-2 text-xs font-semibold text-white hover:bg-black"
                            >
                              Clear search
                            </button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  )}
                </Droppable>
              </DragDropContext>
            </table>

              {/* Delete confirm modal */}
              {jobPendingDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                  <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Delete job</h3>
                    <p className="text-xs text-gray-600 mb-3">
                      Are you sure you want to permanently delete this job?
                    </p>
                    <p className="text-xs text-gray-800 font-medium mb-4">
                      {jobPendingDelete.customer_name} — {jobPendingDelete.job_location || 'No location'}
                    </p>
                    <div className="flex justify-end gap-2 text-xs">
                      <button
                        type="button"
                        className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                        onClick={() => setJobPendingDelete(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                        onClick={async () => {
                          const jobToDelete = jobPendingDelete;
                          setJobPendingDelete(null);
                          await handleDeleteJob(jobToDelete);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
          </div>
        </div>

  );
};

// Commencement Form Component

const CommencementForm = ({ job, onBack, onSubmit }) => {
  const [formData, setFormData] = useState({
    job_id: job?.id || '',
    customer_name: job?.customer_name || '',
    address: job?.job_location || '',
    supervisor: '',
    start_date: new Date().toISOString().split('T')[0],
    job_types: [],
    underground_utilities: [
      { question: 'Have all utility locates been completed?', answer: 'no', comment: '' },
      { question: 'Gas line/Propane Line', answer: 'no', comment: '' },
      { question: 'Hydro Above or Underground', answer: 'no', comment: '' },
      { question: 'Communication', answer: 'no', comment: '' },
      { question: 'Sewer', answer: 'no', comment: '' },
      { question: 'Septic', answer: 'no', comment: '' },
      { question: 'Water', answer: 'no', comment: '' },
      { question: 'Private Utilities (Irrigation, garage or shed feeds)', answer: 'no', comment: '' },
      { question: 'Other', answer: 'no', comment: '' }
    ],
    site_walkthrough_notes: '',
    measurement_notes: '',
    utility_entry_notes: '',
    photos: [],
    pre_existing_damage_notes: '',
    damage_photos: [],
    hazards: [
      { category: 'Gravity', hazard: '', control: '' },
      { category: 'Motion', hazard: '', control: '' },
      { category: 'Mechanical', hazard: '', control: '' },
      { category: 'Chemical', hazard: '', control: '' },
      { category: 'Environmental', hazard: '', control: '' }
    ],
    supervisor_signature: '',
    crew_members: [''],
    user_email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [loadedFormStatus, setLoadedFormStatus] = useState('');

  // Load latest form (draft or submitted) so teams can edit and resave without re-entering.
  useEffect(() => {
    const loadLatestForm = async () => {
      try {
        const response = await axios.get(`${API}/forms/commencement/latest/${job?.id}`);
        if (response.data) {
          const incoming = response.data || {};
          setFormData(prev => ({
            ...prev,
            ...incoming,
            underground_utilities: Array.isArray(incoming.underground_utilities) && incoming.underground_utilities.length
              ? incoming.underground_utilities
              : prev.underground_utilities,
            hazards: Array.isArray(incoming.hazards) && incoming.hazards.length
              ? incoming.hazards
              : prev.hazards,
            crew_members: Array.isArray(incoming.crew_members) && incoming.crew_members.length
              ? incoming.crew_members
              : prev.crew_members,
          }));
          setDraftLoaded(true);
          setLoadedFormStatus(incoming.status || 'submitted');
        }
      } catch (error) {
        console.error('Error loading latest commencement form:', error);
        // No previous form found or error - continue with defaults
      }
    };
    
    if (job?.id) {
      loadLatestForm();
    }
  }, [job?.id]);

  const isEditingExistingForm = draftLoaded && !!formData.id;

  const jobTypeOptions = [
    'Waterproofing',
    'Piers',
    'Foundation Repair',
    'SmartJack',
    'PowerBrace',
    'GeoLocks',
    'Septic',
    'Other'
  ];

  const handleJobTypeChange = (jobType, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        job_types: [...prev.job_types, jobType]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        job_types: prev.job_types.filter(type => type !== jobType)
      }));
    }
  };

  const handleUtilityAnswerChange = (index, answer) => {
    const newUtilities = [...formData.underground_utilities];
    newUtilities[index].answer = answer;
    // Clear comment if switching to "no"
    if (answer === 'no') {
      newUtilities[index].comment = '';
    }
    setFormData(prev => ({ ...prev, underground_utilities: newUtilities }));
  };

  const handleUtilityCommentChange = (index, comment) => {
    const newUtilities = [...formData.underground_utilities];
    newUtilities[index].comment = comment;
    setFormData(prev => ({ ...prev, underground_utilities: newUtilities }));
  };

  const handleHazardChange = (index, field, value) => {
    const newHazards = [...formData.hazards];
    newHazards[index][field] = value;
    setFormData(prev => ({ ...prev, hazards: newHazards }));
  };

  const addCrewMember = () => {
    setFormData(prev => ({
      ...prev,
      crew_members: [...prev.crew_members, '']
    }));
  };

  const updateCrewMember = (index, value) => {
    const newMembers = [...formData.crew_members];
    newMembers[index] = value;
    setFormData(prev => ({ ...prev, crew_members: newMembers }));
  };

  const removeCrewMember = (index) => {
    setFormData(prev => ({
      ...prev,
      crew_members: prev.crew_members.filter((_, i) => i !== index)
    }));
  };

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    
    try {
      const draftData = {
        ...formData,
        status: 'draft'
      };
      
      await axios.post(`${API}/forms/commencement`, draftData);
      alert('💾 Draft saved successfully! You can continue later.');
      setDraftLoaded(true);
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Error saving draft. Please try again.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate underground utilities section
    const hasIncompleteUtility = formData.underground_utilities.some(utility => {
      return utility.answer === 'yes' && (!utility.comment || utility.comment.trim() === '');
    });
    
    if (hasIncompleteUtility) {
      alert('⚠️ Please provide location/details for all utilities marked as "Yes" before submitting.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        status: 'submitted'
      };
      
      await axios.post(`${API}/forms/commencement`, submitData);
      alert('✅ Commencement form submitted successfully! Email sent to ' + formData.user_email);
      onSubmit();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('❌ Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
          <h1 className="text-3xl font-bold text-gray-800">COMMENCEMENT FORM</h1>
          <div></div>
        </div>

        {isEditingExistingForm && (
          <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="commencement-editing-banner">
            Loaded existing {loadedFormStatus === 'draft' ? 'draft' : 'submitted'} form. Edit and save/submit to update it.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Basic Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Customer Name"
                value={formData.customer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="text"
                placeholder="Address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="text"
                placeholder="Supervisor"
                value={formData.supervisor}
                onChange={(e) => setFormData(prev => ({ ...prev, supervisor: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="email"
                placeholder="Your Email"
                value={formData.user_email}
                onChange={(e) => setFormData(prev => ({ ...prev, user_email: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
            </div>
          </div>

          {/* Job Types */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Select Job Types</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {jobTypeOptions.map((jobType) => (
                <label key={jobType} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.job_types.includes(jobType)}
                    onChange={(e) => handleJobTypeChange(jobType, e.target.checked)}
                    className="w-5 h-5 text-blue-600"
                  />
                  <span className="text-gray-800">{jobType}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Underground Utilities */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">🔧 Underground Utilities</h2>
            <p className="text-sm text-gray-600 mb-6">Please indicate if any of the following utilities are present. If "Yes", you must describe the location.</p>
            <div className="space-y-4">
              {formData.underground_utilities.map((utility, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  {/* Question and Yes/No Toggle */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                    <span className="text-gray-800 font-medium">{utility.question}</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleUtilityAnswerChange(index, 'no')}
                        className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                          utility.answer === 'no'
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        No
                      </button>
                      <button
                        type="button"
                        onClick={() => handleUtilityAnswerChange(index, 'yes')}
                        className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                          utility.answer === 'yes'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        Yes
                      </button>
                    </div>
                  </div>
                  
                  {/* Comment Field (shown when Yes is selected) */}
                  {utility.answer === 'yes' && (
                    <div className="mt-3">
                      <textarea
                        placeholder="📍 Describe the location (Required)"
                        value={utility.comment}
                        onChange={(e) => handleUtilityCommentChange(index, e.target.value)}
                        className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                        rows="2"
                        required={utility.answer === 'yes'}
                      />
                      {utility.answer === 'yes' && (!utility.comment || utility.comment.trim() === '') && (
                        <p className="text-red-600 text-sm mt-1">⚠️ Location description is required when "Yes" is selected</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Site Assessment */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Site Walkthrough and Initial Assessments</h2>
            <div className="space-y-4">
              <textarea
                placeholder="Site Walkthrough Notes"
                value={formData.site_walkthrough_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, site_walkthrough_notes: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800 h-32"
                required
              />
              <textarea
                placeholder="Measurement Notes & Utility Entry"
                value={formData.measurement_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, measurement_notes: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800 h-32"
              />
              <textarea
                placeholder="Pre-Existing Damage Documentation"
                value={formData.pre_existing_damage_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, pre_existing_damage_notes: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800 h-32"
              />
            </div>
          </div>

          {/* Hazard Identification */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Hazard Identification</h2>
            <div className="space-y-4">
              {formData.hazards.map((hazard, index) => (
                <div key={index} className="grid md:grid-cols-3 gap-4 p-4 bg-gray-700 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{hazard.category}</label>
                  </div>
                  <input
                    type="text"
                    placeholder="Describe Hazard"
                    value={hazard.hazard}
                    onChange={(e) => handleHazardChange(index, 'hazard', e.target.value)}
                    className="w-full p-3 rounded-lg bg-gray-600 text-white"
                  />
                  <input
                    type="text"
                    placeholder="Control Measures"
                    value={hazard.control}
                    onChange={(e) => handleHazardChange(index, 'control', e.target.value)}
                    className="w-full p-3 rounded-lg bg-gray-600 text-white"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Crew Members */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Crew Members</h2>
            <div className="space-y-4">
              {formData.crew_members.map((member, index) => (
                <div key={index} className="flex gap-4">
                  <input
                    type="text"
                    placeholder={`Crew Member ${index + 1}`}
                    value={member}
                    onChange={(e) => updateCrewMember(index, e.target.value)}
                    className="flex-1 p-4 rounded-lg bg-gray-700 text-white"
                    required
                  />
                  {formData.crew_members.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCrewMember(index)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addCrewMember}
                className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-bold"
              >
                + Add Crew Member
              </button>
            </div>
          </div>

          {/* Signature */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Supervisor Sign-Off</h2>
            <input
              type="text"
              placeholder="Supervisor Digital Signature (Type Full Name)"
              value={formData.supervisor_signature}
              onChange={(e) => setFormData(prev => ({ ...prev, supervisor_signature: e.target.value }))}
              className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
              required
            />
          </div>

          {/* Save Draft and Submit Buttons */}
          <div className="space-y-4">
            {/* Save Draft Button */}
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={isSavingDraft}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 py-4 rounded-xl font-bold text-xl text-white transition-colors"
              data-testid="commencement-save-draft-btn"
            >
              {isSavingDraft ? '💾 Saving Draft...' : isEditingExistingForm ? '💾 Update Draft' : '💾 Save Draft'}
            </button>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 py-4 rounded-xl font-bold text-xl text-white transition-colors"
              data-testid="commencement-submit-btn"
            >
              {isSubmitting ? '✅ Submitting...' : isEditingExistingForm ? '✅ Update Commencement Form' : '✅ Submit Commencement Form'}
            </button>
          </div>

        </form>
        </div>
      </div>
  );
};

// Completion Form Component
const CompletionForm = ({ job, existingForm = null, onBack, onSubmit }) => {
  const [formData, setFormData] = useState({
    job_id: job?.id || '',
    customer_name: job?.customer_name || '',
    property_address: job?.job_location || '',
    date_of_completion: new Date().toISOString().split('T')[0],
    overall_satisfaction: 'Yes',
    work_completion: 'Yes',
    quality_of_work: 'Yes',
    cleanliness: 'Yes',
    communication: 'Yes',
    property_damage: 'No',
    future_maintenance: 'Yes',
    final_walkthrough: 'Yes',
    review_incentive: 'Yes',
    media_consent: 'Yes',
    additional_comments: '',
    customer_signature: '',
    user_email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadedForEdit, setIsLoadedForEdit] = useState(false);
  const signatureCanvasRef = React.useRef(null);
  const isDrawingRef = React.useRef(false);

  const initSignatureCanvas = React.useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  useEffect(() => {
    initSignatureCanvas();
  }, [initSignatureCanvas]);

  useEffect(() => {
    if (!existingForm) {
      setIsLoadedForEdit(false);
      return;
    }

    setFormData(prev => ({
      ...prev,
      ...existingForm,
    }));
    setIsLoadedForEdit(true);
  }, [existingForm]);

  const getCanvasPoint = (event) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;
    return { x, y };
  };

  const handleSignatureStart = (event) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const point = getCanvasPoint(event);
    if (!point) return;
    isDrawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const handleSignatureMove = (event) => {
    if (!isDrawingRef.current) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const point = getCanvasPoint(event);
    if (!point) return;
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const handleSignatureEnd = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    setFormData(prev => ({ ...prev, customer_signature: dataUrl }));
  };

  const handleSignatureClear = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setFormData(prev => ({ ...prev, customer_signature: '' }));
  };

  const yesNoFields = [
    { key: 'overall_satisfaction', label: 'Overall Satisfaction' },
    { key: 'work_completion', label: 'Work Completion' },
    { key: 'quality_of_work', label: 'Quality of Work' },
    { key: 'cleanliness', label: 'Cleanliness' },
    { key: 'communication', label: 'Communication' },
    { key: 'property_damage', label: 'Property Damage' },
    { key: 'future_maintenance', label: 'Future Maintenance' },
    { key: 'final_walkthrough', label: 'Final Walkthrough' },
    { key: 'review_incentive', label: 'Would you be willing to provide a 5 star Google review to save $100.00 on your estimate?' },
    { key: 'media_consent', label: 'Media Consent' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_signature) {
      alert('Please capture the customer signature before submitting.');
      return;
    }
    setIsSubmitting(true);
    
    try {
      const res = await axios.post(`${API}/forms/completion`, formData);
      alert((res.data?.message || 'Completion form submitted successfully') + '! Email sent to ' + formData.user_email);
      onSubmit();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
          <h1 className="text-3xl font-bold text-gray-800">COMPLETION FORM</h1>
          <div></div>
        </div>

        {isLoadedForEdit && (
          <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="completion-editing-banner">
            Loaded existing completion form. Update fields and submit to save changes.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Basic Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Customer Name"
                value={formData.customer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="text"
                placeholder="Property Address"
                value={formData.property_address}
                onChange={(e) => setFormData(prev => ({ ...prev, property_address: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="date"
                value={formData.date_of_completion}
                onChange={(e) => setFormData(prev => ({ ...prev, date_of_completion: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="email"
                placeholder="Your Email"
                value={formData.user_email}
                onChange={(e) => setFormData(prev => ({ ...prev, user_email: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
            </div>
          </div>

          {/* Customer Feedback Sections */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Customer Feedback</h2>
            <div className="space-y-6">
              {yesNoFields.map((field) => (
                <div key={field.key} className={`flex flex-col gap-4 ${
                  field.key === 'review_incentive' ? 'lg:flex-col' : 'md:flex-row md:items-center'
                }`}>
                  <label className={`text-gray-800 font-medium ${
                    field.key === 'review_incentive' ? 'w-full' : 'md:w-1/3'
                  }`}>
                    {field.label}:
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, [field.key]: 'Yes' }))}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                        formData[field.key] === 'Yes'
                          ? 'bg-green-600 hover:bg-green-700 text-white shadow-md'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                      }`}
                    >
                      ✓ Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, [field.key]: 'No' }))}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                        formData[field.key] === 'No'
                          ? 'bg-red-600 hover:bg-red-700 text-white shadow-md'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                      }`}
                    >
                      ✗ No
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Comments */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Additional Comments</h2>
            <textarea
              placeholder="Any additional comments or feedback..."
              value={formData.additional_comments}
              onChange={(e) => setFormData(prev => ({ ...prev, additional_comments: e.target.value }))}
              className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800 h-32"
            />
          </div>

          {/* Customer Signature */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="text-2xl font-bold text-gray-800">Customer Signature</h2>
              <button
                type="button"
                onClick={handleSignatureClear}
                className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="completion-signature-clear"
              >
                Clear Signature
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">Please have the customer sign in the box below.</p>
            <div className="border border-dashed border-gray-300 rounded-lg bg-white p-2">
              <canvas
                ref={signatureCanvasRef}
                width={620}
                height={200}
                onPointerDown={handleSignatureStart}
                onPointerMove={handleSignatureMove}
                onPointerUp={handleSignatureEnd}
                onPointerLeave={handleSignatureEnd}
                onPointerCancel={handleSignatureEnd}
                className="w-full h-48 rounded-md bg-white"
                style={{ touchAction: 'none' }}
                data-testid="completion-signature-canvas"
              />
            </div>
            {!formData.customer_signature && (
              <div className="mt-2 text-xs text-gray-500" data-testid="completion-signature-placeholder">
                Signature not captured yet.
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 py-4 rounded-xl font-bold text-xl"
              data-testid="completion-submit-btn"
            >
              {isSubmitting ? 'Submitting...' : isLoadedForEdit ? 'Update Completion Form' : 'Submit Completion Form'}
            </button>
          </div>

        </form>
        </div>
      </div>
  );
};

// Hazard Assessment Form Component
const HazardAssessmentForm = ({ job, onBack, onSubmit }) => {
  const [formData, setFormData] = useState({
    job_id: job?.id || '',
    customer_name: job?.customer_name || '',
    job_address: job?.job_location || '',
    supervisor: '',
    assessment_date: new Date().toISOString().split('T')[0],
    assessment_time: new Date().toTimeString().slice(0, 5),
    crew_members: [''],
    hazards: [
      { category: 'Gravity', hazard: '', control: '' },
      { category: 'Motion', hazard: '', control: '' },
      { category: 'Mechanical', hazard: '', control: '' },
      { category: 'Chemical', hazard: '', control: '' },
      { category: 'Environmental', hazard: '', control: '' }
    ],
    supervisor_signature: '',
    user_email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleHazardChange = (index, field, value) => {
    const newHazards = [...formData.hazards];
    newHazards[index][field] = value;
    setFormData(prev => ({ ...prev, hazards: newHazards }));
  };

  const addCrewMember = () => {
    setFormData(prev => ({
      ...prev,
      crew_members: [...prev.crew_members, '']
    }));
  };

  const updateCrewMember = (index, value) => {
    const newMembers = [...formData.crew_members];
    newMembers[index] = value;
    setFormData(prev => ({ ...prev, crew_members: newMembers }));
  };

  const removeCrewMember = (index) => {
    setFormData(prev => ({
      ...prev,
      crew_members: prev.crew_members.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/forms/hazard-assessment`, formData);
      alert('Daily Hazard Assessment submitted successfully! Email sent to ' + formData.user_email);
      onSubmit();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
          <h1 className="text-3xl font-bold text-gray-800">DAILY HAZARD ASSESSMENT</h1>
          <div></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Assessment Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Customer Name"
                value={formData.customer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="text"
                placeholder="Job Address"
                value={formData.job_address}
                onChange={(e) => setFormData(prev => ({ ...prev, job_address: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="text"
                placeholder="Supervisor"
                value={formData.supervisor}
                onChange={(e) => setFormData(prev => ({ ...prev, supervisor: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="date"
                value={formData.assessment_date}
                onChange={(e) => setFormData(prev => ({ ...prev, assessment_date: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="time"
                value={formData.assessment_time}
                onChange={(e) => setFormData(prev => ({ ...prev, assessment_time: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
              <input
                type="email"
                placeholder="Your Email"
                value={formData.user_email}
                onChange={(e) => setFormData(prev => ({ ...prev, user_email: e.target.value }))}
                className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                required
              />
            </div>
          </div>

          {/* Crew Members */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Crew Members</h2>
            <div className="space-y-4">
              {formData.crew_members.map((member, index) => (
                <div key={index} className="flex gap-4">
                  <input
                    type="text"
                    placeholder={`Crew Member ${index + 1}`}
                    value={member}
                    onChange={(e) => updateCrewMember(index, e.target.value)}
                    className="flex-1 p-4 rounded-lg bg-gray-700 text-white"
                    required
                  />
                  {formData.crew_members.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCrewMember(index)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addCrewMember}
                className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-bold"
              >
                + Add Crew Member
              </button>
            </div>
          </div>

          {/* Hazard Assessment */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Hazard Identification & Controls</h2>
            <div className="space-y-4">
              {formData.hazards.map((hazard, index) => (
                <div key={index} className="grid md:grid-cols-3 gap-4 p-4 bg-gray-700 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{hazard.category}</label>
                  </div>
                  <input
                    type="text"
                    placeholder="Describe Hazard"
                    value={hazard.hazard}
                    onChange={(e) => handleHazardChange(index, 'hazard', e.target.value)}
                    className="w-full p-3 rounded-lg bg-gray-600 text-white"
                  />
                  <input
                    type="text"
                    placeholder="Control Measures"
                    value={hazard.control}
                    onChange={(e) => handleHazardChange(index, 'control', e.target.value)}
                    className="w-full p-3 rounded-lg bg-gray-600 text-white"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Supervisor Signature */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Supervisor Sign-Off</h2>
            <input
              type="text"
              placeholder="Supervisor Digital Signature (Type Full Name)"
              value={formData.supervisor_signature}
              onChange={(e) => setFormData(prev => ({ ...prev, supervisor_signature: e.target.value }))}
              className="w-full p-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
              required
            />
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 py-4 rounded-xl font-bold text-xl"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Hazard Assessment'}
            </button>
          </div>

        </form>
        </div>
      </div>
  );
};

// Sump Pump Inspection Module with accordion-style steps

// ==================== SEPTIC AS BUILT (Production) ====================


export default JobsModule;
export { CommencementForm, CompletionForm, HazardAssessmentForm };
